import React, { useState, useEffect } from 'react'
import { ComposedChart, Area, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts'
import { useAuth } from '../contexts/AuthContext'
import { saveUserBet, getUserBets, updateUserBet, deleteUserBet } from '../services/userDataService'
import { convertOdds } from '../utils/oddsConverter'
import { BOOKMAKERS_BY_REGION as BOOKMAKERS, BOOKMAKER_LOGOS } from '../config/bookmakers'
import { MARKET_TYPE_MAPPING } from '../config/markets'
import { useAutoSettle } from './useAutoSettle'
import { isHorseBet, settleHorseBet } from '../components/horseBets'
import { liveProgress, liveLegs } from './settlement'
import LiveBetMeter, { LiveMultiMeter } from './LiveBetMeter'
// Explicit, the same as every other page. The bc-lm-* and bc-trk-book-logo rules
// this page relies on live here; they were in src/App.css, which is the stock
// Create React App leftover and is imported by nothing at all.
import '../styles/app.css'

// The date a bet is shown, sorted and filtered by: the game's start when the
// bet has one, otherwise placed_at. Tool bets save placed_at as the moment
// they were tracked, so reading it here would move each one to the day it was
// logged instead of the day its game is on. commence_time is naive UTC with no
// offset, stored with a space ("2026-10-13 08:00:00"), hence the T and the Z:
// Safari refuses the space form outright. Anything unreadable falls back to
// placed_at.
const betDate = (bet) => {
  const t = bet?.commence_time?.replace(' ', 'T')
  if (t) {
    const d = new Date(/Z$|[+-]\d\d:?\d\d$/.test(t) ? t : t + 'Z')
    if (!isNaN(d)) return d
  }
  return new Date(bet?.placed_at)
}

// The edit form's Date field, as it was always prefilled.
const placedDateInput = (bet) =>
  bet?.placed_at ? new Date(bet.placed_at).toISOString().split('T')[0] : new Date().toISOString().split('T')[0]

// Mono is spent only on figures, where it buys column alignment as values change.
// Labels stay on the product's own sans: setting everything in mono is a different
// look, and this is not that.
// Where a bet came in from, and how its badge reads. One entry per tag rather
// than the same five-deep ternary repeated for label, text, fill and border,
// which is four places to miss when a new source ships.
//
// `odds_screen` bets are taken straight off the odds table, so they carry no
// EV figure — the tag is what lets the eye tell those apart from an EV single
// that did have an edge.
const BET_TYPE_BADGE = {
  multi:         { label: 'Multi',     main: '#34d399', bg: 'rgba(16,185,129,0.1)',  border: 'rgba(16,185,129,0.25)' },
  prop_ev_multi: { label: 'Outlier Multi', main: '#34d399', bg: 'rgba(16,185,129,0.1)',  border: 'rgba(16,185,129,0.25)' },
  dabble_pickem: { label: 'PickEm',    main: '#c084fc', bg: 'rgba(168,85,247,0.1)', border: 'rgba(168,85,247,0.25)' },
  prop_ev:       { label: 'Outlier',  main: '#60a5fa', bg: 'rgba(59,130,246,0.1)', border: 'rgba(59,130,246,0.25)' },
  odds_screen:   { label: 'Odds',      main: '#22d3ee', bg: 'rgba(34,211,238,0.1)', border: 'rgba(34,211,238,0.25)' },
  odds_screen_multi: { label: 'Odds Multi', main: '#22d3ee', bg: 'rgba(34,211,238,0.1)', border: 'rgba(34,211,238,0.25)' },
  // Racing Odds page (2026-10): carries race_id + runner_number for settling off race results later.
  racing_odds:   { label: 'Racing',    main: '#a78bfa', bg: 'rgba(167,139,250,0.1)', border: 'rgba(167,139,250,0.25)' },
}

const DEFAULT_BET_TYPE_BADGE = { label: 'Single', main: '#94a3b8', bg: 'rgba(100,116,139,0.1)', border: 'rgba(100,116,139,0.2)' }

export const betTypeBadge = (tag) => BET_TYPE_BADGE[tag] ?? DEFAULT_BET_TYPE_BADGE

// Every bet type the filter knows, in menu order. Counted rather than hardcoded
// so "all types are selected" stays true when a new source ships — the checks
// were literal `=== 5`, which silently reads as "not all" the moment a sixth
// exists, leaving the pill saying nothing is filtered while one type is hidden.
// Bets that came out of one of OUR tools. Every one carries an edge figure, so
// they are the only bets the expected-profit tile and the EV chart line mean
// anything over.
const TOOL_BET_TYPES = ['single', 'multi', 'dabble_pickem', 'prop_ev', 'prop_ev_multi']

// Taken straight off the odds table, with no edge claimed. Kept OUT of the set
// above and never shown alongside it: mixed in, these land in actual profit
// while contributing nothing to expected, so the chart's two lines drift apart
// for a reason that has nothing to do with how the user is betting. Viewing
// them is a mode, not a filter.
const OTHER_BET_TYPES = ['odds_screen', 'odds_screen_multi', 'racing_odds']
const OTHER_LABELS = { odds_screen: 'Odds Screen', odds_screen_multi: 'Odds Multi', racing_odds: 'Racing Odds' }

// Within the Other group the options are ordinary checkboxes, so sports and racing
// bets can be viewed apart. Coming from the tools, picking one starts Other mode
// with just that type; unticking the last one goes back to the tools.
const toggleOtherType = (filter, key, tools) => {
  if (!OTHER_BET_TYPES.some(t => filter.has(t))) return new Set([key])
  const next = new Set(filter)
  next.has(key) ? next.delete(key) : next.add(key)
  return next.size ? next : new Set(tools)
}

const BET_TYPES = [...TOOL_BET_TYPES, ...OTHER_BET_TYPES]

/** True when the current filter is showing the odds-screen set rather than tools. */
const isOtherMode = (filter) => OTHER_BET_TYPES.some(t => filter.has(t))

const MONO = "ui-monospace, 'SF Mono', Menlo, Consolas, 'Liberation Mono', monospace"
const SANS = "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif"

// Surface palette, used app-wide. Page #010714, panels transparent, hairlines
// #1b2538. Panels carry NO fill: they are the page colour with a 1px border, and
// a filled slab is what this is deliberately not.
const s = {
  container: { width: '100%', margin: 0, padding: 0, backgroundColor: '#010714', minHeight: '100vh', height: 'auto', boxSizing: 'border-box' },
  card: { backgroundColor: 'transparent', borderRadius: '0', padding: '16px 20px', marginBottom: '16px' },
  row: { display: 'flex', alignItems: 'center', gap: '20px', flexWrap: 'wrap' },
  title: { fontSize: '28px', fontWeight: '700', margin: 0, color: '#f1f5f9' },
  subtitle: { fontSize: '20px', fontWeight: '600', margin: 0, color: '#f1f5f9' },
  label: { display: 'block', fontWeight: '600', color: '#8a8f96', fontSize: '10.5px', fontFamily: SANS, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '6px' },
  // Label on top, figure on the bottom, both hard left. space-between rather than a
  // plain stack because "Pending Bonus Turnover" wraps to two lines where the others
  // do not, and a wrapped label would otherwise push its own figure out of line with
  // the rest of the row. Pinning the figures to the bottom keeps them on one baseline
  // whatever the label above does.
  // No minHeight: the content sets the height and nothing pads it out. The flex row
  // stretches every card to the tallest anyway, so space-between still holds the
  // figures on one baseline when a long label wraps.
  statCard: { background: 'transparent', borderRadius: '8px', padding: '12px 15px', border: '1px solid #1b2538', flex: '1', minWidth: '140px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', gap: '8px' },
  // The chart and the calendar are the same object, a surface panel, and both
  // used to write their colours inline at the call site. That is what turned a
  // background change into a hunt instead of one edit.
  panel: { backgroundColor: 'transparent', border: '1px solid #1b2538', borderRadius: '10px', padding: '16px' },
  statValue: { fontSize: '21px', fontWeight: '500', margin: 0, lineHeight: '1', fontFamily: MONO, fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.01em' },
  // Sans, not mono, and so it needs tracking back: small uppercase sans sets tight
  // where a mono face is already evenly spaced.
  statLabel: { fontSize: '10.5px', color: '#8a8f96', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.06em', fontFamily: SANS, lineHeight: '1.2' },
  btn: { padding: '12px 24px', backgroundColor: '#2563eb', color: 'white', borderRadius: '8px', border: 'none', cursor: 'pointer', fontWeight: '500' },
  btnSecondary: { padding: '8px 16px', backgroundColor: 'transparent', color: '#f1f5f9', borderRadius: '6px', border: '1px solid #1b2538', cursor: 'pointer', fontSize: '14px' },
  btnActive: { padding: '8px 16px', backgroundColor: '#2563eb', color: 'white', borderRadius: '6px', border: 'none', cursor: 'pointer', fontSize: '14px' },
  // Form fields (add / edit bet, the custom-date picker): the refined modal
  // input surface, #0d1628 with a #1f2b42 border, as on the tool pages.
  input: { border: '1px solid #1f2b42', borderRadius: '8px', padding: '10px 14px', backgroundColor: '#0d1628', color: '#e4e9f0', fontSize: '14px', width: '100%', maxWidth: '100%', boxSizing: 'border-box' },
  select: { border: '1px solid #1f2b42', borderRadius: '8px', padding: '10px 14px', backgroundColor: '#0d1628', color: '#e4e9f0', fontSize: '14px', width: '100%', maxWidth: '100%', boxSizing: 'border-box' },
  table: { width: '100%', borderCollapse: 'collapse' },
  // Sticky because the wrapper scrolls at 400px: without it the header is gone
  // by the third row and every column becomes a guess. The rule underneath is a
  // boxShadow rather than a border because borderCollapse drops the border off a
  // sticky cell, which is a long-standing browser behaviour, not a bug to fix.
  // The background must stay OPAQUE even though every other surface here went
  // transparent: this cell is sticky, and rows scroll underneath it. #010714 is the
  // page colour, so it occludes without reading as a slab.
  th: { padding: '9px 12px', textAlign: 'left', fontWeight: '700', fontSize: '11px', color: '#8a8f96', backgroundColor: '#010714', boxShadow: 'inset 0 -2px 0 #1b2538', textTransform: 'uppercase', letterSpacing: '0.06em', whiteSpace: 'nowrap', position: 'sticky', top: 0, zIndex: 2 },
  // tabular-nums is what makes odds, stakes and P&L read as columns instead of
  // drifting with the width of each digit — the single biggest difference
  // between a table that looks considered and one that does not.
  // Each bet is separated by a real 2px rule rather than a hairline, so rows read
  // as discrete records. The live bet's bracket stays a step brighter again
  // (#4a6795, .bc-lm-row in styles/app.css) so the hierarchy still holds: every bet is
  // divided, and the one in play is framed.
  // 500 rather than 400. Light text on a dark background renders visually
  // thinner than the same weight on white, so a regular weight reads as washed
  // out here; 500 restores the density without tipping into bold, which at 13px
  // across twelve columns would be shouting.
  td: { padding: '9px 12px', borderBottom: '2px solid #1b2538', color: '#e2e8f0', fontSize: '13px', fontWeight: 500, letterSpacing: '-0.005em', fontVariantNumeric: 'tabular-nums' },
  badge: { display: 'inline-block', padding: '3px 9px', borderRadius: '5px', fontSize: '11px', fontWeight: '600', letterSpacing: '0.02em', whiteSpace: 'nowrap' },
  badgeGreen: { backgroundColor: 'rgba(16,185,129,0.10)', color: '#34d399', border: '1px solid rgba(16,185,129,0.25)' },
  badgeRed: { backgroundColor: 'rgba(239,68,68,0.10)', color: '#f87171', border: '1px solid rgba(239,68,68,0.25)' },
  badgeBlue: { backgroundColor: 'rgba(59,130,246,0.10)', color: '#60a5fa', border: '1px solid rgba(59,130,246,0.25)' },
  badgeGray: { backgroundColor: 'rgba(100,116,139,0.10)', color: '#94a3b8', border: '1px solid rgba(100,116,139,0.20)' },
  // Modals in the same surfaces as the tool pages' refined modals: #080e1b shell
  // over a navy-tinted, lightly blurred scrim, #1b2438 hairlines.
  modalOverlay: { position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: 'rgba(1, 4, 12, 0.66)', backdropFilter: 'blur(2px)', WebkitBackdropFilter: 'blur(2px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modal: { backgroundColor: '#080e1b', borderRadius: '12px', padding: '24px', border: '1px solid #1b2438', boxShadow: '0 24px 72px rgba(0, 0, 0, 0.65)', maxWidth: '800px', width: '95%', maxHeight: '90vh', overflowY: 'auto' },
  modalHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px', borderBottom: '1px solid #1b2438', paddingBottom: '16px' },
  closeBtn: { backgroundColor: 'transparent', border: 'none', color: '#5f6d82', fontSize: '22px', cursor: 'pointer', padding: '4px', lineHeight: 1 },
  dropdownButton: { border: '1px solid #1f2b42', borderRadius: '8px', padding: '10px 14px', backgroundColor: '#0d1628', color: '#e4e9f0', fontSize: '14px', width: '100%', maxWidth: '100%', boxSizing: 'border-box', textAlign: 'left', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  dropdownList: { position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 1000, backgroundColor: '#0d1628', border: '1px solid #1f2b42', borderRadius: '8px', boxShadow: '0 12px 32px rgba(0, 0, 0, 0.55)', maxHeight: '200px', overflowY: 'auto', marginTop: '4px' },
  dropdownItem: { padding: '12px 16px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '12px', borderBottom: '1px solid #1b2438' },
  deleteBtn: { backgroundColor: 'transparent', color: '#8a8f96', border: '1px solid #1b2538', borderRadius: '4px', padding: '4px 8px', cursor: 'pointer', fontSize: '12px' },
  filterBtn: { border: '1px solid #1b2538', borderRadius: '6px', padding: '4px 12px', backgroundColor: 'transparent', color: '#f1f5f9', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', whiteSpace: 'nowrap' },
  dropdown: { position: 'absolute', top: '100%', left: 0, zIndex: 1000, backgroundColor: '#0a1020', border: '1px solid #1b2538', borderRadius: '8px', padding: '16px', boxShadow: '0 8px 24px rgba(0,0,0,0.5)', minWidth: '150px' },
  checkbox: { display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', color: '#f1f5f9', padding: '4px 6px', borderRadius: '8px', border: '1px solid #1b2538', backgroundColor: 'transparent', transition: 'all 0.2s ease', fontSize: '14px', fontWeight: '500' },
  checkLabel: { display: 'flex', alignItems: 'center', gap: '9px', fontSize: '13px', fontWeight: '500', color: '#e2e8f0', fontFamily: SANS, cursor: 'pointer', userSelect: 'none' },
  checkBox: { accentColor: '#3b82f6', width: '15px', height: '15px', cursor: 'pointer', margin: 0 },
  inputGrid: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px 20px' }
}


const SPORT_DISPLAY_NAMES = {
  "NBA": "NBA",
  "WNBA": "WNBA",
  "NBL": "NBL",
  "AFL": "AFL",
  "AFLW": "AFLW",
  "NRL": "NRL",
  "NRLW": "NRLW",
  "RLWC": "RL World Cup",
  "MLS": "MLS",
  "BUNDESLIGA": "Bundesliga",
  "UCL": "UCL",
  "EPL": "EPL",
  "LA_LIGA": "La Liga",
  "SERIE_A": "Serie A",
  "LIGUE_1": "Ligue 1",
  "A_LEAGUE": "A-League",
  "WORLD_CUP": "World Cup",
  "MLB": "MLB",
  "NFL": "NFL",
  "NHL": "NHL",
  "UFC": "UFC",
  "ATP_AUSTRALIAN_OPEN": "ATP Australian Open",
  "ATP_FRENCH_OPEN": "ATP French Open",
  "ATP_WIMBLEDON": "ATP Wimbledon",
  "ATP_US_OPEN": "ATP US Open",
  "ATP_INDIAN_WELLS": "ATP Indian Wells",
  "ATP_MIAMI": "ATP Miami",
  "ATP_MONTE_CARLO": "ATP Monte Carlo",
  "ATP_MADRID": "ATP Madrid",
  "ATP_ROME": "ATP Rome",
  "ATP_CANADA": "ATP Canada",
  "ATP_CINCINNATI": "ATP Cincinnati",
  "ATP_SHANGHAI": "ATP Shanghai",
  "ATP_PARIS": "ATP Paris",
  "ATP_FINALS": "ATP Finals",
  // The whole Challenger tier behind one id, so tracked Challenger bets all
  // carry sport "ATP_CHALLENGER" and group under this one label rather than
  // spawning ~190 competitions a year.
  "ATP_CHALLENGER": "ATP Challenger",
  // Retired ids, display only — bets tracked before the Aug 2026 rename.
  "MENS_AO": "Mens Australian Open",
  "MENS_RG": "Mens Roland Garros",
  "MENS_WIMBLEDON": "Mens Wimbledon",
  "MENS_USO": "Mens US Open",
  "Horse": "Horse Racing",
  "Harness": "Harness Racing",
  "Greyhounds": "Greyhounds"
}

const SPORT_HIERARCHY = {
  "Rugby League": {
    leagues: ["NRL", "NRLW", "RLWC"]
  },
  "Aussie Rules": {
    leagues: ["AFL", "AFLW"]
  },
  "Soccer": {
    leagues: ["MLS", "BUNDESLIGA", "UCL", "EPL", "LA_LIGA", "SERIE_A", "LIGUE_1", "A_LEAGUE", "WORLD_CUP"]
  },
  "Basketball": {
    leagues: ["NBA", "WNBA", "NBL"]
  },
  "Baseball": {
    leagues: ["MLB"]
  },
  "American Football": {
    leagues: ["NFL"]
  },
  "Hockey": {
    leagues: ["NHL"]
  },
  "MMA": {
    leagues: ["UFC"]
  },
  "Tennis": {
    leagues: ["ATP_AUSTRALIAN_OPEN", "ATP_FRENCH_OPEN", "ATP_WIMBLEDON", "ATP_US_OPEN", "ATP_INDIAN_WELLS", "ATP_MIAMI", "ATP_MONTE_CARLO", "ATP_MADRID", "ATP_ROME", "ATP_CANADA", "ATP_CINCINNATI", "ATP_SHANGHAI", "ATP_PARIS", "ATP_FINALS", "ATP_CHALLENGER"]
  },
  "Racing": {
    leagues: ["Horse", "Harness", "Greyhounds"]
  }
}

const formatMarketType = (marketType, playerName = '') => {
  // First check if we have a predefined display format for this market type
  if (MARKET_TYPE_MAPPING[marketType]) {
    return MARKET_TYPE_MAPPING[marketType]
  }

  // Fallback for player props without defined formats
  if (playerName && marketType.includes('player_')) {
    const statType = marketType.replace('player_', '').replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
    return `Player Prop - ${statType}`
  }

  // Final fallback - format the raw market type
  return marketType.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
}

export default function BetTracker2({ isVisible = true }) {
  const { user, oddsFormat } = useAuth()
  // Per-tab bet state: setters are kept in sync but the values are read via allEvBets/allArbBets
  // etc. for display/stats. Unread pending a refactor to drop these in favour of the "all" lists.
  /* eslint-disable no-unused-vars */
  const [evBets, setEvBets] = useState([])
  const [arbBets, setArbBets] = useState([])
  const [promoBets, setPromoBets] = useState([])
  const [middleBets, setMiddleBets] = useState([])
  /* eslint-enable no-unused-vars */

  // All bets for stats calculations (unlimited)
  const [allEvBets, setAllEvBets] = useState([])
  const [allArbBets, setAllArbBets] = useState([])
  const [allPromoBets, setAllPromoBets] = useState([])
  const [allMiddleBets, setAllMiddleBets] = useState([])
  const [activeTab, setActiveTab] = useState('EV')
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(() => Number(localStorage.getItem('tracker_items_per_page')) || 50)
  const [statusFilter, setStatusFilter] = useState('all')
  // eslint-disable-next-line no-unused-vars
  const [loading, setLoading] = useState(true)
  const [timeFilter, setTimeFilter] = useState('all')
  const [customDateStart, setCustomDateStart] = useState('')
  const [customDateEnd, setCustomDateEnd] = useState('')
  const [calendarDate, setCalendarDate] = useState(new Date())
  const [sportFilter, setSportFilter] = useState('all')
  const [bookmakerFilter, setBookmakerFilter] = useState('all')
  const [betTypeFilter, setBetTypeFilter] = useState(new Set(TOOL_BET_TYPES))
  const [showAddForm, setShowAddForm] = useState(false)
  const [showBookmakerDropdown, setShowBookmakerDropdown] = useState(false)
  const [showBookmaker2Dropdown, setShowBookmaker2Dropdown] = useState(false)
  const [showTimeDropdown, setShowTimeDropdown] = useState(false)
  const [showSportDropdown, setShowSportDropdown] = useState(false)
  const [showBookmakerFilterDropdown, setShowBookmakerFilterDropdown] = useState(false)
  const [showBetTypeDropdown, setShowBetTypeDropdown] = useState(false)
  const [editingBetId, setEditingBetId] = useState(null)
  // The edit form only carries user-editable fields, but updateUserBet replaces the
  // whole bet_data blob. Keep the original so everything the form doesn't know about
  // (commence_time, the settlement fields) survives an edit instead of being dropped.
  const [editingBetOriginal, setEditingBetOriginal] = useState(null)
  const [newBet, setNewBet] = useState({ type: activeTab === 'EV' ? 'EV' : activeTab === 'ARB' ? 'ARB' : activeTab === 'MIDDLE' ? 'MIDDLE' : 'PROMO', sport: 'NBA', match: '', selection: '', bookmaker: '', bookmaker2: '', market_type: '', odds: '', stake: '', ev_percentage: '', profit: '', qualifying_loss: '', date: new Date().toISOString().split('T')[0], promo_type: 'Mug Bet', bonus_received: false, bonus_amount: '', free_bonus: false, bet_type_tag: 'single' })
  const [expandedSports, setExpandedSports] = useState(new Set())
  const [showExportConfirm, setShowExportConfirm] = useState(false)
  const [showImportInfo, setShowImportInfo] = useState(false)
  const [importPreview, setImportPreview] = useState(null)
  const [showImportConfirm, setShowImportConfirm] = useState(false)
  const [importLoading, setImportLoading] = useState(false)
  const [importResult, setImportResult] = useState(null)
  const importFileRef = React.useRef(null)


  useEffect(() => {
    const loadUserBets = async () => {
      if (!user?.id) {
        setLoading(false)
        return
      }

      try {
        const result = await getUserBets(user.id, null) // null = get all bets for tracker
        const { data: userBets, error } = result

        if (error) {
          console.error('Error loading user bets:', error)
          setEvBets([])
          setArbBets([])
          setPromoBets([])
          setMiddleBets([])
          setAllEvBets([])
          setAllArbBets([])
          setAllPromoBets([])
          setAllMiddleBets([])
        } else {
          // Separate EV, ARB, PROMO, and MIDDLE bets - ALL DATA for stats
          const allEvBets = userBets.filter(bet => bet.bet_data.type === 'EV').map(bet => ({
            id: bet.id,
            ...bet.bet_data
          }))

          const allArbBets = userBets.filter(bet => bet.bet_data.type === 'ARB').map(bet => ({
            id: bet.id,
            ...bet.bet_data
          }))

          const allPromoBets = userBets.filter(bet => bet.bet_data.type === 'PROMO').map(bet => ({
            id: bet.id,
            ...bet.bet_data
          }))

          const allMiddleBets = userBets.filter(bet => bet.bet_data.type === 'MIDDLE').map(bet => ({
            id: bet.id,
            ...bet.bet_data
          }))

          // Cap recent bets at 50 for table display (but keep all for stats)
          const evBetData = allEvBets.slice(0, 50)
          const arbBetData = allArbBets.slice(0, 50)
          const promoBetData = allPromoBets.slice(0, 50)
          const middleBetData = allMiddleBets.slice(0, 50)

          // Store recent bets for table display
          setEvBets(evBetData)
          setArbBets(arbBetData)
          setPromoBets(promoBetData)
          setMiddleBets(middleBetData)

          // Store all bets for stats calculations
          setAllEvBets(allEvBets)
          setAllArbBets(allArbBets)
          setAllPromoBets(allPromoBets)
          setAllMiddleBets(allMiddleBets)
        }
      } catch (error) {
        console.error('Error loading bets:', error)
        setEvBets([])
        setArbBets([])
        setPromoBets([])
        setMiddleBets([])
        setAllEvBets([])
        setAllArbBets([])
        setAllPromoBets([])
        setAllMiddleBets([])
      } finally {
        setLoading(false)
      }
    }

    loadUserBets()
  }, [user?.id])

  // Update bet type when active tab changes
  useEffect(() => {
    if (!editingBetId) { // Only update if not editing an existing bet
      setNewBet(prev => ({ 
        ...prev, 
        type: activeTab === 'EV' ? 'EV' : activeTab === 'ARB' ? 'ARB' : activeTab === 'MIDDLE' ? 'MIDDLE' : 'PROMO' 
      }))
    }
  }, [activeTab, editingBetId])

  // Database saving happens when bets are added/updated, not on every state change

  // Helper functions for hierarchical sports filter
  const toggleSportCategory = (sportName) => {
    setExpandedSports(prev => {
      const newSet = new Set(prev)
      if (newSet.has(sportName)) {
        newSet.delete(sportName)
      } else {
        newSet.add(sportName)
      }
      return newSet
    })
  }

  const toggleParentSport = (sportName) => {
    const sportData = SPORT_HIERARCHY[sportName]
    if (!sportData) return

    if (isSportFullySelected(sportName)) {
      // Deselect all leagues in this category
      setSportFilter('all')
    } else {
      // For single-select filter, just select the first league in the category
      // Or we could show all leagues - but tracker uses single select not multi-select
      // Let's just set to 'all' to avoid confusion
      setSportFilter('all')
    }
  }

  const isSportFullySelected = (sportName) => {
    const sportData = SPORT_HIERARCHY[sportName]
    if (!sportData) return false
    // Since sportFilter is single-select, check if it matches any league in the category
    return sportData.leagues.includes(sportFilter)
  }

  const isSportPartiallySelected = (sportName) => {
    // Not applicable for single-select, always return false
    return false
  }

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.filter-dropdown') && !event.target.closest('.bookmaker-dropdown')) {
        setShowTimeDropdown(false)
        setShowSportDropdown(false)
        setShowBookmakerFilterDropdown(false)
        setShowBookmakerDropdown(false)
        setShowBookmaker2Dropdown(false)
        setShowBetTypeDropdown(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Helper function to close all dropdowns except the specified one
  const toggleDropdown = (dropdownName) => {
    const wasOpen = {
      time:            showTimeDropdown,
      sport:           showSportDropdown,
      bookmakerFilter: showBookmakerFilterDropdown,
      betType:         showBetTypeDropdown,
      bookmaker:       showBookmakerDropdown,
      bookmaker2:      showBookmaker2Dropdown,
    }[dropdownName]

    setShowTimeDropdown(false)
    setShowSportDropdown(false)
    setShowBookmakerFilterDropdown(false)
    setShowBetTypeDropdown(false)
    setShowBookmakerDropdown(false)
    setShowBookmaker2Dropdown(false)

    if (!wasOpen) {
      switch(dropdownName) {
        case 'time':            setShowTimeDropdown(true);            break
        case 'sport':           setShowSportDropdown(true);           break
        case 'bookmakerFilter': setShowBookmakerFilterDropdown(true); break
        case 'betType':         setShowBetTypeDropdown(true);         break
        case 'bookmaker':       setShowBookmakerDropdown(true);       break
        case 'bookmaker2':      setShowBookmaker2Dropdown(true);      break
        default: break
      }
    }
  }

  const getFilteredBets = () => {
    let bets = activeTab === 'EV' ? allEvBets : activeTab === 'ARB' ? allArbBets : activeTab === 'MIDDLE' ? allMiddleBets : allPromoBets

    // Time filter
    if (timeFilter !== 'all') {
      if (timeFilter === 'custom') {
        if (customDateStart) bets = bets.filter(bet => betDate(bet) >= new Date(customDateStart))
        if (customDateEnd) { const end = new Date(customDateEnd); end.setHours(23,59,59,999); bets = bets.filter(bet => betDate(bet) <= end) }
      } else {
        const now = new Date()
        const cutoffDate = new Date()
        if (timeFilter === '7days') cutoffDate.setDate(now.getDate() - 7)
        if (timeFilter === '1month') cutoffDate.setDate(now.getDate() - 30)
        bets = bets.filter(bet => betDate(bet) >= cutoffDate)
      }
    }

    // Sport filter
    if (sportFilter !== 'all') {
      bets = bets.filter(bet => bet.sport === sportFilter)
    }

    // Bookmaker filter
    if (bookmakerFilter !== 'all') {
      bets = bets.filter(bet => bet.bookmaker === bookmakerFilter || bet.bookmaker2 === bookmakerFilter)
    }

    // Bet type filter (EV only)
    // No "< all types" shortcut. That guard was a hardcoded 5, so the moment a
    // SIXTH type existed the default selection (the five tool types) stopped
    // counting as a partial filter and nothing was filtered at all — which is
    // how odds-screen bets ended up in the table, the stats and the EV chart
    // despite being deselected. Filtering when everything is selected is a
    // no-op anyway, so the shortcut only ever bought a bug.
    if (activeTab === 'EV' && betTypeFilter.size > 0) {
      bets = bets.filter(bet => betTypeFilter.has(bet.bet_type_tag || 'single'))
    }

    // Status filter
    if (statusFilter !== 'all') {
      bets = bets.filter(bet => bet.status === statusFilter)
    }

    return bets
  }

  const getAllFilteredBets = () => {
    // Use ALL bets for stats calculations (not capped at 50)
    let bets = activeTab === 'EV' ? allEvBets : activeTab === 'ARB' ? allArbBets : activeTab === 'MIDDLE' ? allMiddleBets : allPromoBets

    // Apply same filters as table but on complete dataset
    if (timeFilter !== 'all') {
      if (timeFilter === 'custom') {
        if (customDateStart) bets = bets.filter(bet => betDate(bet) >= new Date(customDateStart))
        if (customDateEnd) { const end = new Date(customDateEnd); end.setHours(23,59,59,999); bets = bets.filter(bet => betDate(bet) <= end) }
      } else {
        const now = new Date()
        const cutoffDate = new Date()
        if (timeFilter === '7days') cutoffDate.setDate(now.getDate() - 7)
        if (timeFilter === '1month') cutoffDate.setDate(now.getDate() - 30)
        bets = bets.filter(bet => betDate(bet) >= cutoffDate)
      }
    }

    if (sportFilter !== 'all') {
      bets = bets.filter(bet => bet.sport === sportFilter)
    }

    if (bookmakerFilter !== 'all') {
      bets = bets.filter(bet => bet.bookmaker === bookmakerFilter || bet.bookmaker2 === bookmakerFilter)
    }

    // Bet type filter (EV only)
    // No "< all types" shortcut. That guard was a hardcoded 5, so the moment a
    // SIXTH type existed the default selection (the five tool types) stopped
    // counting as a partial filter and nothing was filtered at all — which is
    // how odds-screen bets ended up in the table, the stats and the EV chart
    // despite being deselected. Filtering when everything is selected is a
    // no-op anyway, so the shortcut only ever bought a bug.
    if (activeTab === 'EV' && betTypeFilter.size > 0) {
      bets = bets.filter(bet => betTypeFilter.has(bet.bet_type_tag || 'single'))
    }

    return bets
  }
  
  const currentBets = getFilteredBets().sort((a, b) => betDate(b) - betDate(a))
  const allCurrentBets = getAllFilteredBets().sort((a, b) => betDate(b) - betDate(a))

  const totalPages = Math.ceil(currentBets.length / itemsPerPage)
  const paginatedBets = currentBets.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)

  // Reset to page 1 when tab or filters change
  useEffect(() => { setCurrentPage(1) }, [activeTab, timeFilter, sportFilter, bookmakerFilter, betTypeFilter, statusFilter])

  // Auto-settle pending bets against the results feed. Only EV bets can be
  // settled this way (ARB/MIDDLE/PROMO carry a known profit at entry), so only
  // those lists need patching when a result comes back. Makes no requests when
  // nothing is pending. See useAutoSettle.js.
  const autoSettleBets = React.useMemo(
    () => [...allEvBets, ...allArbBets, ...allPromoBets, ...allMiddleBets],
    [allEvBets, allArbBets, allPromoBets, allMiddleBets]
  )

  // Which multis have had their legs opened. Collapsed is the default, so this
  // holds the exceptions rather than the state of every multi, and a bet that
  // leaves the list simply stops being in it. Ids, not indexes, so paging or a
  // re-sort cannot open somebody else's bet.
  const [openMultis, setOpenMultis] = useState(() => new Set())
  const toggleMulti = React.useCallback((id) => {
    setOpenMultis(prev => {
      const next = new Set(prev)
      if (!next.delete(id)) next.add(id)
      return next
    })
  }, [])

  // liveGames maps bet id -> the fixture it matched, used to show where an
  // in-progress bet currently stands. Display only; settlement is the hook's job.
  const { liveGames } = useAutoSettle(autoSettleBets, React.useCallback((applied) => {
    const byId = new Map(applied.map(a => [a.id, a]))
    const patch = (list) => list.map(b => (byId.has(b.id) ? { ...b, ...byId.get(b.id) } : b))
    setEvBets(patch)
    setAllEvBets(patch)
  }, []))

  const stats = React.useMemo(() => {
    // Use ALL bets for stats calculations (complete dataset)
    const settledBets = allCurrentBets.filter(bet => bet.status !== 'pending')
    const totalStake = allCurrentBets.reduce((sum, bet) => sum + bet.stake, 0)
    const totalProfit = settledBets.reduce((sum, bet) => sum + (bet.actual_profit || 0), 0)
    const theoreticalEV = allCurrentBets.reduce((sum, bet) => {
      if (bet.ev_percentage && activeTab === 'EV') {
        return sum + ((bet.ev_percentage / 100) * bet.stake)
      }
      return sum
    }, 0)
    const roi = totalStake > 0 ? (totalProfit / totalStake) * 100 : 0
    const winRate = settledBets.length > 0 ? (settledBets.filter(bet => bet.status === 'won').length / settledBets.length) * 100 : 0
    
    // PROMO specific stats - use ALL promo bets for accurate calculations
    let promoStats = {}
    if (activeTab === 'PROMO') {
      const bonusReceived = allPromoBets.filter(bet => bet.bonus_received).reduce((sum, bet) => sum + (bet.bonus_amount || 0), 0)
      const bonusCleared = allPromoBets.filter(bet => bet.promo_type === 'Bonus Turnover' && !bet.free_bonus).reduce((sum, bet) => sum + bet.stake, 0)
      const pendingBonusTurnover = bonusReceived - bonusCleared

      // Bonus retention: profit vs stake from all bonus turnover bets
      const bonusTurnoverBets = allPromoBets.filter(bet => bet.promo_type === 'Bonus Turnover' && bet.status !== 'pending')
      const bonusTurnoverStake = bonusTurnoverBets.reduce((sum, bet) => sum + (bet.stake || 0), 0)
      const bonusTurnoverProfit = bonusTurnoverBets.reduce((sum, bet) => sum + (bet.actual_profit || 0), 0)
      const bonusRetention = bonusTurnoverStake > 0 ? (bonusTurnoverProfit / bonusTurnoverStake) * 100 : 0

      const mugBets = allPromoBets.filter(bet => bet.promo_type === 'Mug Bet').length
      const promoBetsCount = allPromoBets.filter(bet => bet.promo_type === 'Promo Bet').length
      const promoMugRatio = mugBets > 0 ? (promoBetsCount / mugBets).toFixed(1) : '0'
      const avgProfitPerBet = allCurrentBets.length > 0 ? totalProfit / allCurrentBets.length : 0

      promoStats = { pendingBonusTurnover, bonusRetention, promoMugRatio, avgProfitPerBet }
    }
    
    return { totalProfit, totalStake, roi, winRate, totalBets: allCurrentBets.length, theoreticalEV, ...promoStats }
  }, [allCurrentBets, activeTab, allPromoBets])

  const chartData = React.useMemo(() => {
    // Use ALL bets for complete profit chart history
    const sortedBets = [...allCurrentBets].filter(bet => bet.status !== 'pending').sort((a, b) => new Date(a.settled_at) - new Date(b.settled_at))
    let runningProfit = 0
    let runningTheoreticalEV = 0
    return sortedBets.map((bet, index) => {
      runningProfit += bet.actual_profit || 0
      if (bet.ev_percentage && activeTab === 'EV') {
        runningTheoreticalEV += (bet.ev_percentage / 100) * bet.stake
      }
      return { 
        bet: index + 1, 
        profit: runningProfit, 
        theoreticalEV: activeTab === 'EV' ? runningTheoreticalEV : null,
        date: new Date(bet.settled_at).toLocaleDateString() 
      }
    })
  }, [allCurrentBets, activeTab])

  const calendarDailyProfits = React.useMemo(() => {
    const map = {}
    allCurrentBets.filter(b => b.status !== 'pending').forEach(bet => {
      const d = new Date(bet.settled_at || bet.placed_at)
      const key = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`
      map[key] = (map[key] || 0) + (bet.actual_profit || 0)
    })
    return map
  }, [allCurrentBets])

  const addBet = async () => {
    if (!newBet.match || !newBet.stake) {
      return
    }
    if (newBet.type === 'EV' && (!newBet.odds || !newBet.selection || !newBet.bookmaker)) {
      return
    }
    if (newBet.type === 'ARB' && (!newBet.bookmaker || !newBet.bookmaker2)) {
      return
    }
    if (newBet.type === 'PROMO' && !newBet.bookmaker) {
      return
    }
    
    const bet = { 
      ...newBet, 
      odds: newBet.odds ? parseFloat(newBet.odds) : null, 
      stake: parseFloat(newBet.stake), 
      ev_percentage: newBet.ev_percentage ? parseFloat(newBet.ev_percentage) : null,
      bonus_amount: newBet.bonus_amount ? parseFloat(newBet.bonus_amount) : null,
      status: (newBet.type === 'ARB' || newBet.type === 'PROMO' || newBet.type === 'MIDDLE') && newBet.profit ? (parseFloat(newBet.profit) > 0 ? 'won' : 'lost') : 'pending',
      actual_profit: (newBet.type === 'ARB' || newBet.type === 'PROMO' || newBet.type === 'MIDDLE') && newBet.profit ? parseFloat(newBet.profit) : null,
      settled_at: (newBet.type === 'ARB' || newBet.type === 'PROMO' || newBet.type === 'MIDDLE') && newBet.profit ? new Date(newBet.date).toISOString() : null, 
      placed_at: new Date(newBet.date).toISOString()
    }

    // Save to database
    if (user?.id) {
      try {
        const result = await saveUserBet(user.id, bet)
        const { data: savedBet, error } = result

        if (error) {
          console.error('Error saving bet:', error)
          alert('Error saving bet: ' + error)
          return
        }
        
        // Update local state with database bet (which has the proper ID)
        const dbBet = {
          id: savedBet.id,
          ...savedBet.bet_data
        }

        if (bet.type === 'EV') { setEvBets(prev => [...prev, dbBet]); setAllEvBets(prev => [...prev, dbBet]) }
        else if (bet.type === 'ARB') { setArbBets(prev => [...prev, dbBet]); setAllArbBets(prev => [...prev, dbBet]) }
        else if (bet.type === 'MIDDLE') { setMiddleBets(prev => [...prev, dbBet]); setAllMiddleBets(prev => [...prev, dbBet]) }
        else { setPromoBets(prev => [...prev, dbBet]); setAllPromoBets(prev => [...prev, dbBet]) }
      } catch (err) {
        console.error('Exception saving bet:', err)
        alert('Exception saving bet: ' + err.message)
        return
      }
    } else {
      // Fallback to local state if no user
      if (bet.type === 'EV') { setEvBets(prev => [...prev, bet]); setAllEvBets(prev => [...prev, bet]) }
      else if (bet.type === 'ARB') { setArbBets(prev => [...prev, bet]); setAllArbBets(prev => [...prev, bet]) }
      else if (bet.type === 'MIDDLE') { setMiddleBets(prev => [...prev, bet]); setAllMiddleBets(prev => [...prev, bet]) }
      else { setPromoBets(prev => [...prev, bet]); setAllPromoBets(prev => [...prev, bet]) }
    }

    closeModal()
  }

  const editBet = (betId) => {
    const allBets = [...allEvBets, ...allArbBets, ...allPromoBets, ...allMiddleBets]
    const betToEdit = allBets.find(bet => bet.id === betId)
    if (betToEdit) {
      setNewBet({
        type: betToEdit.type,
        sport: betToEdit.sport,
        match: betToEdit.match,
        selection: betToEdit.selection || '',
        bookmaker: betToEdit.bookmaker,
        bet_type_tag: betToEdit.bet_type_tag || 'single',
        bookmaker2: betToEdit.bookmaker2 || '',
        market_type: betToEdit.market_type || '',
        odds: betToEdit.odds || '',
        stake: betToEdit.stake || '',
        ev_percentage: betToEdit.ev_percentage || '',
        profit: '',
        // A bet with a game time shows that game's date, read only (see the
        // Date field); the rest keep the placed date they always had.
        date: betToEdit.commence_time ? betDate(betToEdit).toLocaleDateString('en-CA') : placedDateInput(betToEdit),
        promo_type: betToEdit.promo_type || 'Mug Bet',
        bonus_received: betToEdit.bonus_received || false,
        bonus_amount: betToEdit.bonus_amount || '',
        free_bonus: betToEdit.free_bonus || false,
        qualifying_loss: betToEdit.qualifying_loss || ''
      })
      setEditingBetId(betId)
      setEditingBetOriginal(betToEdit)
      setShowAddForm(true)
    }
  }

  const updateBet = async () => {
    if (!newBet.match || !newBet.stake) return
    if (newBet.type === 'EV' && (!newBet.odds || !newBet.selection || !newBet.bookmaker)) return
    if (newBet.type === 'ARB' && (!newBet.bookmaker || !newBet.bookmaker2)) return
    if (newBet.type === 'PROMO' && !newBet.bookmaker) return
    
    const updatedBetData = {
      // Original first so fields the form does not carry are preserved; the form
      // values below then overwrite everything the user can actually change.
      ...(editingBetOriginal || {}),
      ...newBet,
      odds: newBet.odds ? parseFloat(newBet.odds) : null,
      stake: parseFloat(newBet.stake),
      ev_percentage: newBet.ev_percentage ? parseFloat(newBet.ev_percentage) : null,
      bonus_amount: newBet.bonus_amount ? parseFloat(newBet.bonus_amount) : null,
      status: (newBet.type === 'ARB' || newBet.type === 'PROMO' || newBet.type === 'MIDDLE') && newBet.profit ? (parseFloat(newBet.profit) > 0 ? 'won' : 'lost') : 'pending',
      actual_profit: (newBet.type === 'ARB' || newBet.type === 'PROMO' || newBet.type === 'MIDDLE') && newBet.profit ? parseFloat(newBet.profit) : null,
      settled_at: (newBet.type === 'ARB' || newBet.type === 'PROMO' || newBet.type === 'MIDDLE') && newBet.profit ? new Date(newBet.date).toISOString() : null,
      // Kept as saved unless the user actually moved the date: rewriting it on
      // every edit reset the exact tracked time to midnight. A bet with a game
      // time cannot move its date at all.
      placed_at: (editingBetOriginal?.placed_at && (editingBetOriginal.commence_time || newBet.date === placedDateInput(editingBetOriginal)))
        ? editingBetOriginal.placed_at
        : new Date(newBet.date).toISOString()
    }
    delete updatedBetData.id  // id lives on the row, not inside bet_data

    // Update in database
    if (user?.id) {
      const { error } = await updateUserBet(editingBetId, updatedBetData)
      if (error) {
        console.error('Error updating bet:', error)
        return
      }
    }
    
    // Update local state
    const updatedBet = { 
      id: editingBetId,
      ...updatedBetData
    }
    
    if (newBet.type === 'EV') {
      setEvBets(prev => prev.map(bet => bet.id === editingBetId ? updatedBet : bet))
      setAllEvBets(prev => prev.map(bet => bet.id === editingBetId ? updatedBet : bet))
    } else if (newBet.type === 'ARB') {
      setArbBets(prev => prev.map(bet => bet.id === editingBetId ? updatedBet : bet))
      setAllArbBets(prev => prev.map(bet => bet.id === editingBetId ? updatedBet : bet))
    } else if (newBet.type === 'MIDDLE') {
      setMiddleBets(prev => prev.map(bet => bet.id === editingBetId ? updatedBet : bet))
      setAllMiddleBets(prev => prev.map(bet => bet.id === editingBetId ? updatedBet : bet))
    } else {
      setPromoBets(prev => prev.map(bet => bet.id === editingBetId ? updatedBet : bet))
      setAllPromoBets(prev => prev.map(bet => bet.id === editingBetId ? updatedBet : bet))
    }
    closeModal()
  }

  const closeModal = () => {
    setShowAddForm(false)
    setShowBookmakerDropdown(false)
    setShowBookmaker2Dropdown(false)
    setShowTimeDropdown(false)
    setShowSportDropdown(false)
    setShowBookmakerFilterDropdown(false)
    setEditingBetId(null)
    setEditingBetOriginal(null)
    setNewBet({ type: activeTab === 'EV' ? 'EV' : activeTab === 'ARB' ? 'ARB' : activeTab === 'MIDDLE' ? 'MIDDLE' : 'PROMO', sport: 'NBA', match: '', selection: '', bookmaker: '', bookmaker2: '', market_type: '', odds: '', stake: '', ev_percentage: '', profit: '', qualifying_loss: '', date: new Date().toISOString().split('T')[0], promo_type: 'Mug Bet', bonus_received: false, bonus_amount: '', free_bonus: false, bet_type_tag: 'single' })
  }

  const selectBookmaker = (bookmaker, field = 'bookmaker') => {
    setNewBet({...newBet, [field]: bookmaker})
    setShowBookmakerDropdown(false)
    setShowBookmaker2Dropdown(false)
  }

  const updateBetStatus = async (betId, status) => {
    // Find the bet to update
    const allBets = [...allEvBets, ...allArbBets, ...allPromoBets, ...allMiddleBets]
    const betToUpdate = allBets.find(bet => bet.id === betId)
    if (!betToUpdate) {
      console.error('Bet not found:', betId)
      return
    }
    
    // Calculate updated bet data
    let updatedBetData
    if (isHorseBet(betToUpdate)) {
      // Horse Racing Bot bets: status here is won / placed / lost
      updatedBetData = settleHorseBet(betToUpdate, status)
    } else if (betToUpdate.type === 'ARB' || betToUpdate.type === 'PROMO' || betToUpdate.type === 'MIDDLE') {
      // For ARB, PROMO, and MIDDLE bets, handle profit appropriately
      if (betToUpdate.type === 'MIDDLE' && status === 'lost') {
        // For MIDDLE bets that are lost, use negative qualifying loss
        updatedBetData = { 
          ...betToUpdate, 
          status, 
          actual_profit: -(betToUpdate.qualifying_loss || 0),
          settled_at: status !== 'pending' ? new Date().toISOString() : null 
        }
      } else {
        // For ARB, PROMO, and won MIDDLE bets, keep existing profit
        updatedBetData = { 
          ...betToUpdate, 
          status, 
          settled_at: status !== 'pending' ? new Date().toISOString() : null 
        }
      }
    } else {
      // For EV bets, calculate profit based on won/lost
      updatedBetData = { 
        ...betToUpdate, 
        status, 
        actual_profit: status === 'won' ? (betToUpdate.odds - 1) * betToUpdate.stake : status === 'lost' ? -betToUpdate.stake : null, 
        settled_at: status !== 'pending' ? new Date().toISOString() : null
      }
    }

    // Update in database
    if (user?.id) {
      const { error } = await updateUserBet(betId, updatedBetData)
      if (error) {
        console.error('Error updating bet status:', error)
        alert('Error updating bet status: ' + error)
        return
      }
    }

    // Update local state based on bet type, not active tab
    if (betToUpdate.type === 'EV') {
      setEvBets(prev => prev.map(bet => bet.id === betId ? updatedBetData : bet))
      setAllEvBets(prev => prev.map(bet => bet.id === betId ? updatedBetData : bet))
    } else if (betToUpdate.type === 'ARB') {
      setArbBets(prev => prev.map(bet => bet.id === betId ? updatedBetData : bet))
      setAllArbBets(prev => prev.map(bet => bet.id === betId ? updatedBetData : bet))
    } else if (betToUpdate.type === 'MIDDLE') {
      setMiddleBets(prev => prev.map(bet => bet.id === betId ? updatedBetData : bet))
      setAllMiddleBets(prev => prev.map(bet => bet.id === betId ? updatedBetData : bet))
    } else {
      setPromoBets(prev => prev.map(bet => bet.id === betId ? updatedBetData : bet))
      setAllPromoBets(prev => prev.map(bet => bet.id === betId ? updatedBetData : bet))
    }
  }

  const executeExport = () => {
    const tabBets = activeTab === 'EV' ? allEvBets : activeTab === 'ARB' ? allArbBets : activeTab === 'MIDDLE' ? allMiddleBets : allPromoBets

    // date is the one the table shows (game date when known). placed_at is the
    // exact moment the bet was tracked and game_time the game's start, both
    // full UTC timestamps; blank where a bet does not have one.
    const dateOf = b => (b.commence_time || b.placed_at) ? betDate(b).toLocaleDateString('en-CA') : ''
    const placedOf = b => b.placed_at ? new Date(b.placed_at).toISOString() : ''
    const gameOf = b => b.commence_time ? betDate(b).toISOString() : ''

    let headers, rows
    if (activeTab === 'EV') {
      headers = ['date', 'placed_at', 'game_time', 'match', 'sport', 'bookmaker', 'bet_type', 'market_type', 'selection', 'odds', 'stake', 'ev_percentage', 'status', 'profit']
      rows = tabBets.map(b => [
        dateOf(b), placedOf(b), gameOf(b),
        b.match || '', b.sport || '', b.bookmaker || '',
        b.bet_type_tag || 'single', b.market_type || '', b.selection || '',
        b.odds || '', b.stake || '', b.ev_percentage || '',
        b.status || '', b.actual_profit ?? ''
      ])
    } else if (activeTab === 'ARB') {
      headers = ['date', 'placed_at', 'game_time', 'match', 'sport', 'bookmaker', 'bookmaker2', 'stake', 'profit', 'status']
      rows = tabBets.map(b => [
        dateOf(b), placedOf(b), gameOf(b),
        b.match || '', b.sport || '', b.bookmaker || '', b.bookmaker2 || '',
        b.stake || '', b.actual_profit ?? '', b.status || ''
      ])
    } else if (activeTab === 'MIDDLE') {
      headers = ['date', 'placed_at', 'game_time', 'match', 'sport', 'bookmaker', 'bookmaker2', 'stake', 'qualifying_loss', 'profit', 'status']
      rows = tabBets.map(b => [
        dateOf(b), placedOf(b), gameOf(b),
        b.match || '', b.sport || '', b.bookmaker || '', b.bookmaker2 || '',
        b.stake || '', b.qualifying_loss || '', b.actual_profit ?? '', b.status || ''
      ])
    } else {
      headers = ['date', 'match', 'sport', 'bookmaker', 'promo_type', 'stake', 'bonus_received', 'bonus_amount', 'free_bonus', 'status', 'profit']
      rows = tabBets.map(b => [
        b.placed_at ? new Date(b.placed_at).toISOString().split('T')[0] : '',
        b.match || '', b.sport || '', b.bookmaker || '', b.promo_type || '',
        b.stake || '', b.bonus_received ? 'true' : 'false',
        b.bonus_amount || '', b.free_bonus ? 'true' : 'false',
        b.status || '', b.actual_profit ?? ''
      ])
    }

    const escape = val => {
      const str = String(val)
      return str.includes(',') || str.includes('"') || str.includes('\n') ? `"${str.replace(/"/g, '""')}"` : str
    }

    const csv = [headers, ...rows].map(row => row.map(escape).join(',')).join('\n')
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `bets-${activeTab}-${new Date().toISOString().split('T')[0]}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  const IMPORT_FIELDS = {
    EV: [
      { field: 'date',          required: true,  example: '2026-03-27',       desc: 'Bet date (YYYY-MM-DD)' },
      { field: 'match',         required: true,  example: 'Lakers vs Celtics', desc: 'Match or event name' },
      { field: 'sport',         required: true,  example: 'NBA',              desc: 'Sport or league' },
      { field: 'stake',         required: true,  example: '50.00',            desc: 'Amount staked' },
      { field: 'bookmaker',     required: true,  example: 'Bet365',           desc: 'Bookmaker used' },
      { field: 'odds',          required: true,  example: '2.50',             desc: 'Decimal odds' },
      { field: 'status',        required: true,  example: 'won',              desc: 'won, lost or pending. Pending bets are settled by hand, never automatically' },
      { field: 'profit',        required: true,  example: '75.00',            desc: 'Actual profit or loss. Negative for a loss e.g. -50.00. Leave blank for pending' },
      { field: 'selection',     required: false, example: 'Lakers',           desc: 'Your selection' },
      { field: 'market_type',   required: false, example: 'head to head',     desc: 'Market type' },
      { field: 'bet_type',      required: false, example: 'single',           desc: 'single, multi, or dabble_pickem. Defaults to single' },
      { field: 'ev_percentage', required: false, example: '4.2',              desc: 'Edge percentage, no % sign' },
      { field: 'placed_at',  required: false, example: '2026-03-27T09:23:15Z', desc: 'Exact time the bet was logged. Defaults to date' },
      { field: 'game_time',  required: false, example: '2026-03-27T17:00:00Z', desc: 'Game start time' },
    ],
    ARB: [
      { field: 'date',       required: true,  example: '2026-03-27',       desc: 'Bet date (YYYY-MM-DD)' },
      { field: 'match',      required: true,  example: 'Lakers vs Celtics', desc: 'Match or event name' },
      { field: 'sport',      required: true,  example: 'NBA',              desc: 'Sport or league' },
      { field: 'stake',      required: true,  example: '100.00',           desc: 'Amount staked' },
      { field: 'bookmaker',  required: true,  example: 'Bet365',           desc: 'Bookmaker for leg 1' },
      { field: 'bookmaker2', required: true,  example: 'Sportsbet',        desc: 'Bookmaker for leg 2' },
      { field: 'status',     required: true,  example: 'won',              desc: 'won, lost or pending. Pending bets are settled by hand, never automatically' },
      { field: 'profit',     required: true,  example: '5.00',             desc: 'Actual profit or loss. Negative for a loss e.g. -5.00. Leave blank for pending' },
      { field: 'placed_at',  required: false, example: '2026-03-27T09:23:15Z', desc: 'Exact time the bet was logged. Defaults to date' },
      { field: 'game_time',  required: false, example: '2026-03-27T17:00:00Z', desc: 'Game start time' },
    ],
    MIDDLE: [
      { field: 'date',            required: true,  example: '2026-03-27',       desc: 'Bet date (YYYY-MM-DD)' },
      { field: 'match',           required: true,  example: 'Lakers vs Celtics', desc: 'Match or event name' },
      { field: 'sport',           required: true,  example: 'NBA',              desc: 'Sport or league' },
      { field: 'stake',           required: true,  example: '100.00',           desc: 'Amount staked' },
      { field: 'bookmaker',       required: true,  example: 'Bet365',           desc: 'Bookmaker for leg 1' },
      { field: 'bookmaker2',      required: true,  example: 'Sportsbet',        desc: 'Bookmaker for leg 2' },
      { field: 'status',          required: true,  example: 'won',              desc: 'won, lost or pending. Pending bets are settled by hand, never automatically' },
      { field: 'profit',          required: true,  example: '45.00',            desc: 'Actual profit or loss. Negative for a loss e.g. -2.00. Leave blank for pending' },
      { field: 'qualifying_loss', required: false, example: '2.00',             desc: 'Max loss if middle misses' },
      { field: 'placed_at',  required: false, example: '2026-03-27T09:23:15Z', desc: 'Exact time the bet was logged. Defaults to date' },
      { field: 'game_time',  required: false, example: '2026-03-27T17:00:00Z', desc: 'Game start time' },
    ],
    PROMO: [
      { field: 'date',           required: true,  example: '2026-03-27',       desc: 'Date placed (YYYY-MM-DD)' },
      { field: 'match',          required: true,  example: 'Lakers vs Celtics', desc: 'Match or event name' },
      { field: 'sport',          required: true,  example: 'NBA',              desc: 'Sport or league' },
      { field: 'stake',          required: true,  example: '50.00',            desc: 'Amount staked' },
      { field: 'bookmaker',      required: true,  example: 'Bet365',           desc: 'Bookmaker used' },
      { field: 'status',         required: true,  example: 'won',              desc: 'won, lost or pending. Pending bets are settled by hand, never automatically' },
      { field: 'profit',         required: true,  example: '12.00',            desc: 'Actual profit or loss. Negative for a loss e.g. -10.00. Leave blank for pending' },
      { field: 'promo_type',     required: false, example: 'Promo Bet',         desc: 'Mug Bet, Promo Bet, or Bonus Turnover — defaults to Promo Bet' },
      { field: 'bonus_received', required: false, example: 'false',            desc: 'true or false' },
      { field: 'bonus_amount',   required: false, example: '25.00',            desc: 'Bonus amount received' },
      { field: 'free_bonus',     required: false, example: 'false',            desc: 'true or false' },
    ]
  }

  const handleFileSelect = (e) => {
    const file = e.target.files[0]
    if (!file) return
    e.target.value = ''

    const reader = new FileReader()
    reader.onload = (evt) => {
      const text = evt.target.result
      const lines = text.trim().split('\n')
      if (lines.length < 2) { alert('CSV appears empty or has no data rows.'); return }

      const headers = lines[0].split(',').map(h => h.trim().replace(/"/g, '').toLowerCase())
      const dataLines = lines.slice(1, 1001)
      const required = IMPORT_FIELDS[activeTab].filter(f => f.required).map(f => f.field)

      const valid = []
      const skipped = []

      dataLines.forEach((line, i) => {
        if (!line.trim()) return

        // Parse line handling quoted values
        const values = []
        let cur = '', inQ = false
        for (const c of line) {
          if (c === '"') inQ = !inQ
          else if (c === ',' && !inQ) { values.push(cur.trim()); cur = '' }
          else cur += c
        }
        values.push(cur.trim())

        const row = {}
        headers.forEach((h, idx) => { row[h] = values[idx] !== undefined ? values[idx].replace(/"/g, '') : '' })

        const status = row.status?.toLowerCase()

        // Check required fields. A pending bet has no result yet, so no profit.
        const missing = required.filter(f => !row[f] && !(f === 'profit' && status === 'pending'))
        if (missing.length > 0) { skipped.push({ row: i + 2, reason: `Missing: ${missing.join(', ')}` }); return }

        const date = new Date(row.date)
        if (isNaN(date)) { skipped.push({ row: i + 2, reason: 'Invalid date' }); return }

        const stake = parseFloat(row.stake)
        if (isNaN(stake) || stake <= 0) { skipped.push({ row: i + 2, reason: 'Invalid stake' }); return }

        // All tabs: won and lost need a valid profit; pending carries none
        if (!['won', 'lost', 'pending'].includes(status)) { skipped.push({ row: i + 2, reason: 'Status must be won, lost or pending' }); return }
        const isPending = status === 'pending'
        const profit = isPending ? null : parseFloat(row.profit)
        if (!isPending && isNaN(profit)) { skipped.push({ row: i + 2, reason: 'Profit must be a valid number' }); return }

        // Optional exact times, as the export writes them. Blank is fine; a
        // value that is there but unreadable is refused rather than dropped.
        // PROMO has neither, so they are ignored on that tab.
        const timed = activeTab !== 'PROMO'
        const placedAt = timed && row.placed_at ? new Date(row.placed_at) : null
        if (placedAt && isNaN(placedAt)) { skipped.push({ row: i + 2, reason: 'Invalid placed_at' }); return }
        const gameTime = timed && row.game_time ? new Date(row.game_time) : null
        if (gameTime && isNaN(gameTime)) { skipped.push({ row: i + 2, reason: 'Invalid game_time' }); return }

        let bet = {
          type: activeTab,
          sport: row.sport,
          match: row.match,
          bookmaker: row.bookmaker,
          stake,
          status,
          actual_profit: profit,
          placed_at: (placedAt || date).toISOString(),
          settled_at: isPending ? null : date.toISOString(),
          // Stored the way the feeds store it: naive UTC, no offset.
          ...(gameTime ? { commence_time: gameTime.toISOString().slice(0, 19) } : {}),
          is_external: true
        }

        if (activeTab === 'EV') {
          const odds = parseFloat(row.odds)
          if (isNaN(odds) || odds <= 1) { skipped.push({ row: i + 2, reason: 'Odds must be a decimal number greater than 1' }); return }
          const evPct = row.ev_percentage ? parseFloat(row.ev_percentage) : null
          const validBetTypes = BET_TYPES
          const betType = validBetTypes.includes(row.bet_type) ? row.bet_type : 'single'
          bet = { ...bet, odds, selection: row.selection || '', market_type: row.market_type || '', bet_type_tag: betType, ev_percentage: (evPct !== null && !isNaN(evPct)) ? evPct : null }
        } else if (activeTab === 'ARB') {
          bet = { ...bet, bookmaker2: row.bookmaker2 }
        } else if (activeTab === 'MIDDLE') {
          bet = { ...bet, bookmaker2: row.bookmaker2, qualifying_loss: row.qualifying_loss ? parseFloat(row.qualifying_loss) : null }
        } else {
          bet = { ...bet, promo_type: row.promo_type || 'Promo Bet', bonus_received: row.bonus_received === 'true', bonus_amount: row.bonus_amount ? parseFloat(row.bonus_amount) : null, free_bonus: row.free_bonus === 'true' }
        }

        valid.push(bet)
      })

      setImportPreview({ valid, skipped })
      setShowImportInfo(false)
      setShowImportConfirm(true)
    }
    reader.readAsText(file)
  }

  const executeImport = async () => {
    if (!importPreview?.valid?.length || !user?.id) return
    setImportLoading(true)

    let imported = 0
    let failed = 0

    for (const bet of importPreview.valid) {
      try {
        const result = await saveUserBet(user.id, bet)
        if (result.error) { failed++; continue }
        const dbBet = { id: result.data.id, ...result.data.bet_data }
        if (bet.type === 'EV') { setEvBets(prev => [...prev, dbBet]); setAllEvBets(prev => [...prev, dbBet]) }
        else if (bet.type === 'ARB') { setArbBets(prev => [...prev, dbBet]); setAllArbBets(prev => [...prev, dbBet]) }
        else if (bet.type === 'MIDDLE') { setMiddleBets(prev => [...prev, dbBet]); setAllMiddleBets(prev => [...prev, dbBet]) }
        else { setPromoBets(prev => [...prev, dbBet]); setAllPromoBets(prev => [...prev, dbBet]) }
        imported++
      } catch { failed++ }
    }

    setImportLoading(false)
    setShowImportConfirm(false)
    setImportPreview(null)
    setImportResult({ imported, skipped: (importPreview.skipped?.length || 0) + failed })
    setTimeout(() => setImportResult(null), 5000)
  }

  const deleteBet = async (betId) => {
    // Delete from database
    if (user?.id) {
      const result = await deleteUserBet(betId)

      if (result.error) {
        console.error('Error deleting bet:', result.error)
        alert('Error deleting bet: ' + result.error)
        return
      }
    }

    // Update local state
    if (activeTab === 'EV') { setEvBets(prev => prev.filter(bet => bet.id !== betId)); setAllEvBets(prev => prev.filter(bet => bet.id !== betId)) }
    else if (activeTab === 'ARB') { setArbBets(prev => prev.filter(bet => bet.id !== betId)); setAllArbBets(prev => prev.filter(bet => bet.id !== betId)) }
    else if (activeTab === 'MIDDLE') { setMiddleBets(prev => prev.filter(bet => bet.id !== betId)); setAllMiddleBets(prev => prev.filter(bet => bet.id !== betId)) }
    else { setPromoBets(prev => prev.filter(bet => bet.id !== betId)); setAllPromoBets(prev => prev.filter(bet => bet.id !== betId)) }
  }

  return (
    <div className="tracker-container" style={s.container}>
      <style>{`
        /* Global body scrollbar styling */
        body::-webkit-scrollbar,
        html::-webkit-scrollbar {
          width: 6px;
          height: 6px;
        }
        body::-webkit-scrollbar-track,
        html::-webkit-scrollbar-track {
          background: #010714;
        }
        body::-webkit-scrollbar-thumb,
        html::-webkit-scrollbar-thumb {
          background: transparent;
          border-radius: 3px;
          transition: background 0.2s;
        }
        body:hover::-webkit-scrollbar-thumb,
        html:hover::-webkit-scrollbar-thumb {
          background: #475569;
        }
        body::-webkit-scrollbar-thumb:hover,
        html::-webkit-scrollbar-thumb:hover {
          background: #64748b !important;
        }

        /* Custom scrollbar for hover-only visibility */
        .tracker-scrollable::-webkit-scrollbar,
        .tracker-modal::-webkit-scrollbar,
        .tracker-container::-webkit-scrollbar {
          width: 6px;
          height: 6px;
        }
        .tracker-scrollable::-webkit-scrollbar-track,
        .tracker-modal::-webkit-scrollbar-track,
        .tracker-container::-webkit-scrollbar-track {
          background: #010714;
        }
        .tracker-scrollable::-webkit-scrollbar-thumb,
        .tracker-modal::-webkit-scrollbar-thumb,
        .tracker-container::-webkit-scrollbar-thumb {
          background: transparent;
          border-radius: 3px;
          transition: background 0.2s;
        }
        .tracker-scrollable:hover::-webkit-scrollbar-thumb,
        .tracker-modal:hover::-webkit-scrollbar-thumb,
        .tracker-container:hover::-webkit-scrollbar-thumb {
          background: #475569;
        }
        .tracker-scrollable::-webkit-scrollbar-thumb:hover,
        .tracker-modal::-webkit-scrollbar-thumb:hover,
        .tracker-container::-webkit-scrollbar-thumb:hover {
          background: #64748b !important;
        }

        /* Firefox */
        body,
        html {
          scrollbar-width: thin;
          scrollbar-color: transparent #010714;
        }
        body:hover,
        html:hover {
          scrollbar-color: #475569 #010714;
        }
        .tracker-scrollable,
        .tracker-modal,
        .tracker-container {
          scrollbar-width: thin;
          scrollbar-color: transparent #010714;
        }
        .tracker-scrollable:hover,
        .tracker-modal:hover,
        .tracker-container:hover {
          scrollbar-color: #475569 #010714;
        }
      `}</style>
      <style>{`
        .page-title {
          font-size: 22px;
          font-weight: 600;
          margin: 0;
          color: #f1f5f9;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', sans-serif;
          letter-spacing: -0.01em;
        }

        .dropdown-utility-btn {
          padding: 4px 10px;
          background: transparent;
          color: #8a8f96;
          border: 1px solid #1b2538;
          border-radius: 4px;
          cursor: pointer;
          font-size: 11px;
          font-weight: 600;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', sans-serif;
          transition: all 0.15s ease;
          text-transform: uppercase;
          letter-spacing: 0.03em;
        }

        .dropdown-utility-btn:hover {
          background: #223050;
          border-color: #94a3b8;
        }

        .tab-btn {
          padding: 6px 4px;
          border: none;
          border-bottom: 2px solid transparent;
          border-radius: 0;
          cursor: pointer;
          font-size: 14px;
          font-weight: 500;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', sans-serif;
          transition: color 0.2s ease, border-color 0.2s ease;
          color: #64748b;
          background: transparent;
          letter-spacing: 0.01em;
          white-space: nowrap;
        }

        .tab-btn:hover {
          color: #94a3b8;
        }

        .tab-btn.active {
          color: #f1f5f9;
          font-weight: 600;
          border-bottom: 2px solid #3b82f6;
        }

        .filter-button {
          border: 1px solid #1b2538;
          border-radius: 6px;
          padding: 8px 14px;
          background: transparent;
          color: #f1f5f9;
          cursor: pointer;
          font-size: 13px;
          font-weight: 500;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', sans-serif;
          letter-spacing: 0.01em;
          transition: all 0.15s ease;
          display: flex;
          align-items: center;
          gap: 8px;
          justify-content: space-between;
        }

        .filter-button:hover {
          background: rgba(255, 255, 255, 0.03);
          border-color: #35486b;
        }

        .header-btn {
          padding: 10px 16px;
          font-size: 13px;
          font-weight: 500;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', sans-serif;
          background: transparent;
          color: #f1f5f9;
          border: 1px solid #1b2538;
          border-radius: 6px;
          cursor: pointer;
          transition: all 0.15s ease;
          letter-spacing: 0.01em;
          display: flex;
          align-items: center;
          gap: 7px;
        }
        .header-btn:hover {
          background: rgba(255, 255, 255, 0.03);
          border-color: #35486b;
        }

        /* Pagination lives in styles/app.css as .bc-pagination / .bc-page-arrow, shared
           with every other tool page. Nothing local here on purpose. */

        .table-btn-edit {
          padding: 5px 10px;
          background: transparent;
          color: #60a5fa;
          border: 1px solid rgba(59, 130, 246, 0.35);
          border-radius: 5px;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 5px;
          font-size: 11px;
          font-weight: 500;
          transition: all 0.15s ease;
        }
        .table-btn-edit:hover {
          background: #1e3a5f;
          border-color: #3b82f6;
          color: #93c5fd;
        }
        .table-btn-edit:hover svg path {
          stroke: #93c5fd;
        }

        /* Won / Lost / QL. Tinted like the status badge rather than filled, and given a
           real hover: as inline styles they had none, so the only affordance on a
           destructive one-click action was the cursor. */
        .table-btn-won,
        .table-btn-lost {
          padding: 5px 8px;
          border-radius: 5px;
          cursor: pointer;
          display: flex;
          align-items: center;
          background: transparent;
          transition: all 0.15s ease;
        }
        .table-btn-won {
          color: #34d399;
          border: 1px solid rgba(16, 185, 129, 0.25);
        }
        .table-btn-won:hover {
          background: rgba(16, 185, 129, 0.12);
          border-color: rgba(16, 185, 129, 0.5);
        }
        .table-btn-lost {
          color: #f87171;
          border: 1px solid rgba(239, 68, 68, 0.25);
        }
        .table-btn-lost:hover {
          background: rgba(239, 68, 68, 0.12);
          border-color: rgba(239, 68, 68, 0.5);
        }

        .table-btn-delete {
          padding: 5px 10px;
          background: transparent;
          color: #8a8f96;
          border: 1px solid #1b2538;
          border-radius: 5px;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 5px;
          font-size: 11px;
          font-weight: 500;
          transition: all 0.15s ease;
        }
        .table-btn-delete:hover {
          background: #2d1515;
          border-color: #b91c1c;
          color: #fca5a5;
        }
        .table-btn-delete:hover svg path {
          stroke: #fca5a5;
        }
      `}</style>
      <div style={s.card}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px', gap: '12px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '14px', flex: 1, minWidth: 0 }}>
            <h1 className="page-title" style={{ flexShrink: 0 }}>Bet Tracker</h1>
            <div style={{ width: '1px', height: '20px', backgroundColor: '#1b2538', flexShrink: 0 }} />
            <div style={{ display: 'inline-flex', gap: '20px', flexShrink: 0 }}>
              {[['EV','EV'],['ARB','Arbitrage'],['MIDDLE','Middle'],['PROMO','Promo']].map(([val, label]) => (
                <button key={val} onClick={() => setActiveTab(val)} className={`tab-btn${activeTab === val ? ' active' : ''}`}>{label}</button>
              ))}
            </div>
            <div style={{ width: '1px', height: '22px', backgroundColor: '#1b2538', flexShrink: 0 }} />
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'nowrap' }}>
          <div className="filter-dropdown" style={{ position: 'relative' }}>
            <button onClick={() => toggleDropdown('time')} className="filter-button" style={{ minWidth: '80px' }}>
              <span>{timeFilter === 'all' ? 'All Time' : timeFilter === '7days' ? '7 Days' : timeFilter === '1month' ? '1 Month' : 'Custom'}</span>
              <span>{showTimeDropdown ? '▲' : '▾'}</span>
            </button>
            {showTimeDropdown && (
              <div style={s.dropdown}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <div 
                    onClick={() => {setTimeFilter('all'); setShowTimeDropdown(false)}} 
                    style={{
                      ...s.checkbox,
                      border: timeFilter === 'all' ? '2px solid #3b82f6' : '1px solid #1b2538',
                      backgroundColor: timeFilter === 'all' ? 'rgba(59, 130, 246, 0.12)' : 'transparent'
                    }}
                  >
                    <span>All Time</span>
                  </div>
                  <div 
                    onClick={() => {setTimeFilter('7days'); setShowTimeDropdown(false)}} 
                    style={{
                      ...s.checkbox,
                      border: timeFilter === '7days' ? '2px solid #3b82f6' : '1px solid #1b2538',
                      backgroundColor: timeFilter === '7days' ? 'rgba(59, 130, 246, 0.12)' : 'transparent'
                    }}
                  >
                    <span>7 Days</span>
                  </div>
                  <div 
                    onClick={() => {setTimeFilter('1month'); setShowTimeDropdown(false)}} 
                    style={{
                      ...s.checkbox,
                      border: timeFilter === '1month' ? '2px solid #3b82f6' : '1px solid #1b2538',
                      backgroundColor: timeFilter === '1month' ? 'rgba(59, 130, 246, 0.12)' : 'transparent'
                    }}
                  >
                    <span>1 Month</span>
                  </div>
                  <div
                    onClick={() => setTimeFilter('custom')}
                    style={{
                      ...s.checkbox,
                      border: timeFilter === 'custom' ? '2px solid #3b82f6' : '1px solid #1b2538',
                      backgroundColor: timeFilter === 'custom' ? 'rgba(59, 130, 246, 0.12)' : 'transparent'
                    }}
                  >
                    <span>Custom</span>
                  </div>
                  {timeFilter === 'custom' && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', paddingTop: '4px' }}>
                      <input
                        type="date"
                        value={customDateStart}
                        onChange={e => setCustomDateStart(e.target.value)}
                        style={{ ...s.input, padding: '8px 10px', fontSize: '13px' }}
                      />
                      <input
                        type="date"
                        value={customDateEnd}
                        onChange={e => setCustomDateEnd(e.target.value)}
                        style={{ ...s.input, padding: '8px 10px', fontSize: '13px' }}
                      />
                      <button
                        onClick={() => setShowTimeDropdown(false)}
                        style={{ ...s.btn, padding: '8px', fontSize: '12px' }}
                      >
                        Apply
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="filter-dropdown" style={{ position: 'relative' }}>
            <button onClick={() => toggleDropdown('sport')} className="filter-button" style={{ minWidth: '70px' }}>
              <span>{sportFilter === 'all' ? 'Sports' : sportFilter}</span>
              <span>{showSportDropdown ? '▲' : '▾'}</span>
            </button>
            {showSportDropdown && (
              <div style={s.dropdown}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <div
                    onClick={() => {setSportFilter('all'); setShowSportDropdown(false)}}
                    style={{
                      ...s.checkbox,
                      border: sportFilter === 'all' ? '2px solid #3b82f6' : '1px solid #1b2538',
                      backgroundColor: sportFilter === 'all' ? 'rgba(59, 130, 246, 0.12)' : 'transparent'
                    }}
                  >
                    <span>All Sports</span>
                  </div>

                  {/* Hierarchical Sports Categories */}
                  {Object.entries(SPORT_HIERARCHY).map(([sportName, sportData]) => {
                    // Skip Racing category if not in PROMO tab
                    if (sportName === 'Racing' && activeTab !== 'PROMO') return null

                    return (
                      <div key={sportName} style={{ marginBottom: '4px' }}>
                        {/* Parent Sport Category */}
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: '8px',
                          padding: '8px 10px',
                          borderRadius: '6px',
                          backgroundColor: isSportFullySelected(sportName) ? 'rgba(59, 130, 246, 0.15)' : (isSportPartiallySelected(sportName) ? 'rgba(59, 130, 246, 0.08)' : 'transparent'),
                          border: isSportFullySelected(sportName) ? '2px solid #3b82f6' : (isSportPartiallySelected(sportName) ? '1px solid #3b82f6' : '1px solid #1b2538'),
                          cursor: 'pointer'
                        }}>
                          <span onClick={() => toggleParentSport(sportName)} style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            flex: 1,
                            fontSize: '14px',
                            fontWeight: '600',
                            color: '#f1f5f9'
                          }}>
                            <span>{sportName}</span>
                            {isSportPartiallySelected(sportName) && <span style={{ fontSize: '10px', color: '#93c5fd' }}>●</span>}
                          </span>
                          <span onClick={() => toggleSportCategory(sportName)} style={{
                            fontSize: '14px',
                            cursor: 'pointer',
                            userSelect: 'none',
                            color: '#e2e8f0',
                            width: '16px',
                            textAlign: 'center',
                            fontWeight: '700'
                          }}>
                            {expandedSports.has(sportName) ? '▼' : '▶'}
                          </span>
                        </div>

                        {/* Child Leagues (shown when expanded) */}
                        {expandedSports.has(sportName) && (
                          <div style={{ marginLeft: '28px', marginTop: '4px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            {sportData.leagues.map(league => (
                              <div
                                key={league}
                                onClick={() => {setSportFilter(league); setShowSportDropdown(false)}}
                                style={{
                                  padding: '4px 8px',
                                  borderRadius: '4px',
                                  border: sportFilter === league ? '2px solid #3b82f6' : '1px solid #1b2538',
                                  backgroundColor: sportFilter === league ? 'rgba(59, 130, 246, 0.12)' : 'transparent',
                                  cursor: 'pointer',
                                  fontSize: '13px',
                                  color: '#f1f5f9',
                                  fontWeight: '500'
                                }}
                              >
                                {SPORT_DISPLAY_NAMES[league] || league}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              </div>
            )}
          </div>

          {activeTab === 'EV' && (
            <div className="filter-dropdown" style={{ position: 'relative' }}>
              <button onClick={() => toggleDropdown('betType')} className="filter-button" style={{ minWidth: '90px' }}>
                <span>
                  {isOtherMode(betTypeFilter) ? (betTypeFilter.size === 1 ? OTHER_LABELS[[...betTypeFilter][0]] : 'Other') :
                   betTypeFilter.size === TOOL_BET_TYPES.length ? 'All Types' :
                   betTypeFilter.size === 0 ? 'None' :
                   betTypeFilter.size === 1 ? (
                     betTypeFilter.has('single')        ? 'EV Single' :
                     betTypeFilter.has('multi')         ? 'EV Multi' :
                     betTypeFilter.has('dabble_pickem') ? 'PickEm EV' :
                     betTypeFilter.has('prop_ev')       ? 'Prop Outliers' : 'Prop Outlier Multi'
                   ) : `${betTypeFilter.size} Types`}
                </span>
                <span>{showBetTypeDropdown ? '▲' : '▾'}</span>
              </button>
              {showBetTypeDropdown && (
                <div style={s.dropdown}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {/* All toggle. Covers the TOOL types only — "all" means all
                        the bets our tools produced, which is the set the stats
                        below are built for. */}
                    <div
                      onClick={() => setBetTypeFilter(
                        !isOtherMode(betTypeFilter) && betTypeFilter.size === TOOL_BET_TYPES.length
                          ? new Set()
                          : new Set(TOOL_BET_TYPES)
                      )}
                      style={{
                        ...s.checkbox,
                        border: !isOtherMode(betTypeFilter) && betTypeFilter.size === TOOL_BET_TYPES.length ? '2px solid #3b82f6' : '1px solid #1b2538',
                        backgroundColor: !isOtherMode(betTypeFilter) && betTypeFilter.size === TOOL_BET_TYPES.length ? 'rgba(59, 130, 246, 0.12)' : 'transparent',
                        marginBottom: '4px'
                      }}
                    >
                      <span>All Types</span>
                    </div>
                    {[
                      { key: 'single',        label: 'EV Single' },
                      { key: 'multi',         label: 'EV Multi' },
                      { key: 'dabble_pickem', label: 'PickEm EV' },
                      { key: 'prop_ev',       label: 'Prop Outliers' },
                      { key: 'prop_ev_multi', label: 'Prop Outlier Multi' },
                    ].map(({ key, label }) => {
                      const active = betTypeFilter.has(key)
                      return (
                        <div
                          key={key}
                          onClick={() => {
                            // Leaving the odds-screen mode starts a fresh set,
                            // so the two can never end up selected together.
                            const next = isOtherMode(betTypeFilter) ? new Set() : new Set(betTypeFilter)
                            active && !isOtherMode(betTypeFilter) ? next.delete(key) : next.add(key)
                            setBetTypeFilter(next)
                          }}
                          style={{
                            ...s.checkbox,
                            border: active ? '2px solid #3b82f6' : '1px solid #1b2538',
                            backgroundColor: active ? 'rgba(59, 130, 246, 0.12)' : 'transparent',
                            display: 'flex', alignItems: 'center', gap: '8px'
                          }}
                        >
                          <span>{label}</span>
                        </div>
                      )
                    })}

                    {/* Kept apart, and deliberately quiet. These bets came off
                        the odds table rather than a tool, so they carry no edge
                        and cannot share a stats block with the ones above.
                        Selecting one is a MODE SWITCH, not another checkbox. */}
                    <div style={{ margin: '6px 0 2px', paddingTop: '8px', borderTop: '1px solid #1b2538', fontSize: '10px', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#64748b' }}>
                      Other
                    </div>
                    {OTHER_BET_TYPES.map((key) => {
                      const label = OTHER_LABELS[key]
                      const active = betTypeFilter.has(key)
                      return (
                        <div
                          key={key}
                          onClick={() => setBetTypeFilter(toggleOtherType(betTypeFilter, key, TOOL_BET_TYPES))}
                          style={{
                            ...s.checkbox,
                            border: active ? '2px solid #22d3ee' : '1px solid #1b2538',
                            backgroundColor: active ? 'rgba(34,211,238,0.12)' : 'transparent',
                            display: 'flex', alignItems: 'center', gap: '8px'
                          }}
                        >
                          <span>{label}</span>
                        </div>
                      )
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="filter-dropdown" style={{ position: 'relative' }}>
            <button onClick={() => toggleDropdown('bookmakerFilter')} className="filter-button" style={{ minWidth: '100px' }}>
              <span>{bookmakerFilter === 'all' ? 'Bookmakers' : bookmakerFilter}</span>
              <span>{showBookmakerFilterDropdown ? '▲' : '▾'}</span>
            </button>
            {showBookmakerFilterDropdown && (
              <div style={{ ...s.dropdown, minWidth: '200px' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', maxHeight: '400px', overflowY: 'auto' }}>
                  <div 
                    onClick={() => {setBookmakerFilter('all'); setShowBookmakerFilterDropdown(false)}} 
                    style={{
                      ...s.checkbox,
                      border: bookmakerFilter === 'all' ? '2px solid #3b82f6' : '1px solid #1b2538',
                      backgroundColor: bookmakerFilter === 'all' ? 'rgba(59, 130, 246, 0.12)' : 'transparent'
                    }}
                  >
                    <span>All Bookmakers</span>
                  </div>
                  {Object.entries(BOOKMAKERS).map(([country, bookmakers]) => (
                    <div key={country}>
                      <div style={{ fontSize: '12px', color: '#94a3b8', fontWeight: '600', marginBottom: '8px', paddingLeft: '8px' }}>{country}</div>
                      {bookmakers.map(bookmaker => (
                        <div 
                          key={bookmaker} 
                          onClick={() => {setBookmakerFilter(bookmaker); setShowBookmakerFilterDropdown(false)}} 
                          style={{
                            ...s.checkbox,
                            border: bookmakerFilter === bookmaker ? '2px solid #3b82f6' : '1px solid #1b2538',
                            backgroundColor: bookmakerFilter === bookmaker ? 'rgba(59, 130, 246, 0.12)' : 'transparent'
                          }}
                        >
                          {BOOKMAKER_LOGOS[bookmaker] && <img src={BOOKMAKER_LOGOS[bookmaker]} alt={bookmaker} style={{ width: '24px', height: '24px', borderRadius: '4px' }} />}
                          <span>{bookmaker}</span>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>{/* end bookmaker filter-dropdown */}
            </div>{/* end filters */}
          </div>{/* end left side */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
            <input ref={importFileRef} type="file" accept=".csv" style={{ display: 'none' }} onChange={handleFileSelect} />
            <button onClick={() => setShowExportConfirm(true)} className="header-btn">
              <svg width="13" height="13" viewBox="0 0 13 13" fill="none" style={{ flexShrink: 0 }}><path d="M6.5 2v7M3.5 6l3 3 3-3" stroke="#f1f5f9" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/><path d="M2 10.5h9" stroke="#f1f5f9" strokeWidth="1.5" strokeLinecap="round"/></svg>
              Export CSV
            </button>
            <button onClick={() => setShowImportInfo(true)} className="header-btn">
              <svg width="13" height="13" viewBox="0 0 13 13" fill="none" style={{ flexShrink: 0 }}><path d="M6.5 9V2M3.5 5l3-3 3 3" stroke="#f1f5f9" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/><path d="M2 10.5h9" stroke="#f1f5f9" strokeWidth="1.5" strokeLinecap="round"/></svg>
              Import CSV
            </button>
            <button onClick={() => setShowAddForm(true)} className="header-btn">
              <svg width="13" height="13" viewBox="0 0 13 13" fill="none" style={{ flexShrink: 0 }}><path d="M6.5 2v9M2 6.5h9" stroke="#f1f5f9" strokeWidth="1.8" strokeLinecap="round"/></svg>
              Add Bet
            </button>
          </div>
        </div>

        {/* Performance Overview */}
        <div style={{ borderTop: '1px solid #1b2538', marginTop: '16px', paddingTop: '16px' }}>
        <div style={{ ...s.row, gap: '8px' }}>
          <div style={s.statCard}>
            <div style={s.statLabel}>Total Profit</div>
            <div style={{ ...s.statValue, color: stats.totalProfit >= 0 ? '#10b981' : '#ef4444' }}>${stats.totalProfit.toFixed(2)}</div>
          </div>
          {activeTab !== 'PROMO' && (
            <div style={s.statCard}>
              <div style={s.statLabel}>ROI</div>
              <div style={{ ...s.statValue, color: stats.roi >= 0 ? '#10b981' : '#ef4444' }}>{stats.roi.toFixed(1)}%</div>
            </div>
          )}
          {activeTab === 'EV' && !isOtherMode(betTypeFilter) && (
            <div style={s.statCard}>
              <div style={s.statLabel}>Expected Profit</div>
              <div style={{ ...s.statValue, color: '#f59e0b' }}>${stats.theoreticalEV.toFixed(2)}</div>
            </div>
          )}
          <div style={s.statCard}>
            <div style={s.statLabel}>Total Staked</div>
            <div style={{ ...s.statValue, color: '#ffffff' }}>${stats.totalStake.toFixed(2)}</div>
          </div>
          {activeTab === 'PROMO' && (
            <div style={s.statCard}>
              <div style={s.statLabel}>Bonus Retention</div>
              <div style={{ ...s.statValue, color: stats.bonusRetention >= 0 ? '#10b981' : '#ef4444' }}>{stats.bonusRetention.toFixed(1)}%</div>
            </div>
          )}
          {activeTab === 'PROMO' && (
            <div style={s.statCard}>
              <div style={s.statLabel}>Pending Bonus Turnover</div>
              <div style={{ ...s.statValue, color: '#f59e0b' }}>${stats.pendingBonusTurnover.toFixed(2)}</div>
            </div>
          )}
          {activeTab === 'PROMO' && (
            <div style={s.statCard}>
              <div style={s.statLabel}>Promo:Mug Ratio</div>
              <div style={{ ...s.statValue, color: '#ffffff' }}>{stats.promoMugRatio}:1</div>
            </div>
          )}
          <div style={s.statCard}>
            <div style={s.statLabel}>Total Bets</div>
            <div style={{ ...s.statValue, color: '#f59e0b' }}>{stats.totalBets}</div>
          </div>
        </div>
        </div>

        {/* Profit Over Time Chart + Calendar */}
        <div style={{ marginTop: '12px' }}>
        {/* stretch, not flex-start: the chart takes its height from the calendar beside
            it, so the two panels always bottom out together whether the month draws
            five rows of days or six. A fixed chart height cannot do that. */}
        <div style={{ display: 'flex', gap: '24px', alignItems: 'stretch' }}>
        {/* Chart */}
        <div style={{ ...s.panel, flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
          <span style={{ fontSize: '14px', fontWeight: '700', color: '#f1f5f9' }}>Profit Over Time</span>
          {activeTab === 'EV' && (
            // Line swatches rather than dots in pills. The Expected series IS dashed,
            // so a dashed swatch tells you which line is which; a round dot cannot.
            <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '10.5px', fontWeight: '600', color: '#8a8f96', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                <svg width="16" height="2" aria-hidden="true"><line x1="0" y1="1" x2="16" y2="1" stroke="#10b981" strokeWidth="2" /></svg>
                Actual
              </span>
              <span style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '10.5px', fontWeight: '600', color: '#8a8f96', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                <svg width="16" height="2" aria-hidden="true"><line x1="0" y1="1" x2="16" y2="1" stroke="#f59e0b" strokeWidth="2" strokeDasharray="4 3" /></svg>
                Expected
              </span>
            </div>
          )}
        </div>
        {isVisible && (
          <div style={{ flex: 1, minHeight: '380px', width: '100%' }}>
            <ResponsiveContainer width="100%" height="100%" minHeight={380} minWidth={200}>
              <ComposedChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
              {(() => {
                const vals = chartData.map(d => d.profit).filter(v => v != null && !isNaN(v));
                const max = vals.length > 0 ? Math.max(0, ...vals) : 0;
                const min = vals.length > 0 ? Math.min(0, ...vals) : 0;
                const pct = max > min ? Math.round((max / (max - min)) * 100) : 100;
                const color = activeTab === 'EV' ? '#10b981' : '#f59e0b';
                return (
                  <defs>
                    <linearGradient id="profitGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={color} stopOpacity={0.25} />
                      <stop offset={`${pct}%`} stopColor={color} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                );
              })()}
              <CartesianGrid strokeDasharray="3 3" stroke="#1b2538" vertical={false} />
              <XAxis
                dataKey="bet"
                stroke="#475569"
                tick={{ fill: '#8a8f96', fontSize: 11, fontFamily: MONO }}
                axisLine={{ stroke: '#1b2538' }}
                tickLine={false}
                ticks={(() => {
                  const step = chartData.length <= 50 ? 10 : chartData.length <= 200 ? 25 : 100;
                  const result = [0];
                  for (let i = step; i <= chartData.length; i += step) result.push(i);
                  return result;
                })()}
                tickFormatter={(value) => value.toString()}
              />
              <YAxis stroke="#475569" tick={{ fill: '#8a8f96', fontSize: 11, fontFamily: MONO }} axisLine={{ stroke: '#1b2538' }} tickLine={false} tickFormatter={(v) => `$${v}`} width={58} />
              {/* Break-even. On a profit chart this is the one line that answers the
                  question being asked, and the grid alone never marks it. */}
              <ReferenceLine y={0} stroke="#5b6b86" strokeWidth={1} />
              <Tooltip
                contentStyle={{ backgroundColor: '#0a1020', border: '1px solid #1b2538', borderRadius: '8px', color: '#f1f5f9', fontSize: '13px', fontFamily: MONO }}
                cursor={{ stroke: '#475569', strokeWidth: 1 }}
                formatter={(value, name) => {
                  if (name === 'profit') {
                    return [`$${value.toFixed(2)}`, 'Actual Profit']
                  }
                  if (name === 'theoreticalEV') {
                    return [`$${value.toFixed(2)}`, 'Expected Profit']
                  }
                  return [value, name]
                }}
                labelFormatter={() => ''}
              />
              <Area
                type="monotone"
                dataKey="profit"
                stroke={activeTab === 'EV' ? '#10b981' : '#f59e0b'}
                fill="url(#profitGradient)"
                fillOpacity={1}
                strokeWidth={2}
                dot={
                  chartData.length <= 20 ? { fill: activeTab === 'EV' ? '#10b981' : '#f59e0b', strokeWidth: 2, r: 4 } :
                  chartData.length <= 50 ? { fill: activeTab === 'EV' ? '#10b981' : '#f59e0b', strokeWidth: 1, r: 2 } :
                  false
                }
              />
              {/* Dropped in odds-screen mode: none of those bets carry an edge,
                  so the line would sit flat at zero beside a moving profit
                  curve and read as "you are beating expectation" when there was
                  no expectation to beat. */}
              {activeTab === 'EV' && !isOtherMode(betTypeFilter) && (
                <Line
                  type="monotone"
                  dataKey="theoreticalEV"
                  stroke="#f59e0b"
                  strokeWidth={2}
                  strokeDasharray="5 5"
                  dot={false}
                />
              )}
            </ComposedChart>
          </ResponsiveContainer>
        </div>
        )}
        </div>
        {/* Calendar */}
        {(() => {
          const year = calendarDate.getFullYear()
          const month = calendarDate.getMonth()
          const daysInMonth = new Date(year, month + 1, 0).getDate()
          const firstDow = (new Date(year, month, 1).getDay() + 6) % 7 // Mon=0
          const monthName = calendarDate.toLocaleString('default', { month: 'long', year: 'numeric' })
          const days = ['Mo','Tu','We','Th','Fr','Sa','Su']
          const cells = []
          for (let i = 0; i < firstDow; i++) cells.push(null)
          for (let d = 1; d <= daysInMonth; d++) cells.push(d)
          const monthPrefix = `${year}-${String(month+1).padStart(2,'0')}-`
          const monthDays = Object.entries(calendarDailyProfits).filter(([k]) => k.startsWith(monthPrefix))
          const monthTotal = monthDays.reduce((sum, [, v]) => sum + v, 0)
          // Flat fill, won or lost, no scaling by size. The exact figure is on the cell
          // anyway, and the summary underneath names the two days worth knowing.
          const DAY_FILL = 0.45
          const bestDay = monthDays.reduce((b, [, v]) => (v > b ? v : b), 0)
          const worstDay = monthDays.reduce((b, [, v]) => (v < b ? v : b), 0)
          return (
            <div style={{ ...s.panel, width: '360px', flexShrink: 0, display: 'flex', flexDirection: 'column' }}>
              {/* Header */}
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
                <button onClick={() => setCalendarDate(new Date(year, month - 1, 1))} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '28px', height: '28px', borderRadius: '8px', border: '1px solid #1b2538', background: 'transparent', color: '#8a8f96', cursor: 'pointer', flexShrink: 0 }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
                </button>
                <div style={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '0 10px' }}>
                  <span style={{ fontSize: '14px', fontWeight: '700', color: '#f1f5f9' }}>{monthName}</span>
                </div>
                <button onClick={() => setCalendarDate(new Date(year, month + 1, 1))} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '28px', height: '28px', borderRadius: '8px', border: '1px solid #1b2538', background: 'transparent', color: '#8a8f96', cursor: 'pointer', flexShrink: 0 }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>
                </button>
              </div>

              {/* Day headers */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '2px', borderBottom: '1px solid #1b2538', paddingBottom: '8px', marginBottom: '6px' }}>
                {days.map(d => (
                  <div key={d} style={{ textAlign: 'center', fontSize: '10px', color: '#8a8f96', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{d}</div>
                ))}
              </div>

              {/* Day cells */}
              {/* flex:1 with 1fr rows so the day cells absorb whatever slack the chart's
                  height floor leaves us on a short month, instead of the panel ending
                  early and leaving a hole. 52px stays the floor so a six-row month
                  cannot squash them. */}
              <div style={{ flex: 1, display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gridAutoRows: 'minmax(52px, 1fr)', gap: '4px' }}>
                {cells.map((day, i) => {
                  if (!day) return <div key={`e${i}`} />
                  const key = `${year}-${String(month+1).padStart(2,'0')}-${String(day).padStart(2,'0')}`
                  const profit = calendarDailyProfits[key]
                  const hasBets = profit !== undefined
                  const isPositive = profit > 0
                  const today = new Date()
                  const isToday = today.getFullYear() === year && today.getMonth() === month && today.getDate() === day
                  return (
                    <div key={key} title={hasBets ? `$${profit.toFixed(2)}` : 'No bets'} style={{
                      minHeight: '52px',
                      borderRadius: '8px',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between',
                      padding: '4px 6px',
                      boxSizing: 'border-box',
                      borderWidth: '1px',
                      borderStyle: 'solid',
                      // One neutral border on every cell so the grid stays a grid. A day
                      // with bets used to carry a coloured border AND a coloured fill,
                      // which said the same thing twice.
                      borderColor: isToday ? '#3b82f6' : '#1b2538',
                      backgroundColor: hasBets ? `rgba(${isPositive ? '16,185,129' : '239,68,68'},${DAY_FILL})` : 'transparent',
                    }}>
                      <span style={{ fontSize: '11px', fontFamily: MONO, fontWeight: '500', color: hasBets ? (isPositive ? '#6ee7b7' : '#fca5a5') : '#475569' }}>{day}</span>
                      {hasBets && <span style={{ fontSize: '10px', fontFamily: MONO, fontWeight: '600', lineHeight: 1, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums', color: isPositive ? '#34d399' : '#f87171' }}>{isPositive ? '+' : '-'}${Math.abs(profit >= 100 || profit <= -100 ? Math.round(profit) : parseFloat(profit.toFixed(1)))}</span>}
                    </div>
                  )
                })}
              </div>

              {/* Month summary. The grid shows the shape of the month; these are the three
                  figures you cannot read off a coloured cell. Hidden entirely when the
                  month has no bets rather than printing zeroes. */}
              {monthDays.length > 0 && (
                <div style={{ display: 'flex', gap: '12px', borderTop: '1px solid #1b2538', marginTop: '10px', paddingTop: '10px' }}>
                  {[
                    { label: 'Total', value: `${monthTotal >= 0 ? '+' : '-'}$${Math.abs(monthTotal).toFixed(2)}`, color: monthTotal >= 0 ? '#34d399' : '#f87171' },
                    { label: 'Best day', value: `+$${bestDay.toFixed(2)}`, color: '#34d399' },
                    { label: 'Worst day', value: worstDay < 0 ? `-$${Math.abs(worstDay).toFixed(2)}` : '$0.00', color: worstDay < 0 ? '#f87171' : '#8a8f96' }
                  ].map(stat => (
                    <div key={stat.label} style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '9.5px', fontFamily: SANS, fontWeight: '600', color: '#8a8f96', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '4px' }}>{stat.label}</div>
                      <div style={{ fontSize: '13px', fontFamily: MONO, fontWeight: '500', fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.01em', color: stat.color }}>{stat.value}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )
        })()}
        </div>
        </div>

      {showAddForm && (
        <div style={s.modalOverlay} onClick={closeModal}>
          <div className="tracker-modal" style={s.modal} onClick={(e) => e.stopPropagation()}>
            <div style={s.modalHeader}>
              <h2 style={s.subtitle}>{editingBetId ? 'Edit Bet' : 'Add New Bet'}</h2>
              <button onClick={closeModal} style={s.closeBtn}>✕</button>
            </div>
            
            {/* Row 1 */}
            <div style={s.inputGrid}>
              <div>
                <label style={s.label}>Bet Type</label>
                <select value={newBet.type} onChange={(e) => setNewBet({...newBet, type: e.target.value})} style={s.select}>
                  <option value="EV">EV</option>
                  <option value="ARB">Arbitrage</option>
                  <option value="MIDDLE">Middle</option>
                  <option value="PROMO">Promo</option>
                </select>
              </div>
              <div>
                <label style={s.label}>Sport</label>
                <select value={newBet.sport} onChange={(e) => setNewBet({...newBet, sport: e.target.value})} style={s.select}>
                  {Object.entries(SPORT_HIERARCHY).map(([sportName, sportData]) => {
                    // Skip Racing category if not in PROMO tab
                    if (sportName === 'Racing' && newBet.type !== 'PROMO') return null

                    return (
                      <optgroup key={sportName} label={sportName}>
                        {sportData.leagues.map(league => (
                          <option key={league} value={league}>
                            {SPORT_DISPLAY_NAMES[league] || league}
                          </option>
                        ))}
                      </optgroup>
                    )
                  })}
                </select>
              </div>
              <div>
                <label style={s.label}>Date</label>
                <input type="date" value={newBet.date} onChange={(e) => setNewBet({...newBet, date: e.target.value})} disabled={!!(editingBetId && editingBetOriginal?.commence_time)} title={editingBetId && editingBetOriginal?.commence_time ? 'Set by the game\'s start time' : undefined} style={editingBetId && editingBetOriginal?.commence_time ? { ...s.input, opacity: 0.6, cursor: 'not-allowed' } : s.input} />
              </div>
            </div>

            {/* Row 2 */}
            <div style={{...s.inputGrid, marginTop: '16px'}}>
              <div>
                <label style={s.label}>Match</label>
                <input type="text" placeholder="Lakers vs Warriors" value={newBet.match} onChange={(e) => setNewBet({...newBet, match: e.target.value})} style={s.input} />
              </div>
              <div>
                <label style={s.label}>{newBet.type === 'EV' ? 'Selection' : newBet.type === 'PROMO' ? 'Promo Type' : 'Market'}</label>
                {newBet.type === 'PROMO' ? (
                  <select value={newBet.promo_type} onChange={(e) => setNewBet({...newBet, promo_type: e.target.value})} style={s.select}>
                    <option value="Mug Bet">Mug Bet</option>
                    <option value="Promo Bet">Promo Bet</option>
                    <option value="Bonus Turnover">Bonus Turnover</option>
                  </select>
                ) : (
                  <input type="text" placeholder={newBet.type === 'EV' ? "LeBron Over 25.5 Points" : "Head To Head"} value={newBet.type === 'EV' ? newBet.selection : newBet.market_type} onChange={(e) => setNewBet({...newBet, [newBet.type === 'EV' ? 'selection' : 'market_type']: e.target.value})} style={s.input} />
                )}
              </div>
              {newBet.type === 'EV' && (
                <div>
                  <label style={s.label}>Market</label>
                  <input type="text" placeholder="Player Points" value={newBet.market_type} onChange={(e) => setNewBet({...newBet, market_type: e.target.value})} style={s.input} />
                </div>
              )}
              {(newBet.type === 'ARB' || newBet.type === 'MIDDLE') && (
                <div>
                  <label style={s.label}>Total Stake ($)</label>
                  <input type="number" step="0.01" placeholder="200" value={newBet.stake} onChange={(e) => setNewBet({...newBet, stake: e.target.value})} style={s.input} />
                </div>
              )}
              {newBet.type === 'PROMO' && (
                <div>
                  <label style={s.label}>Stake ($)</label>
                  <input type="number" step="0.01" placeholder="100" value={newBet.stake} onChange={(e) => setNewBet({...newBet, stake: e.target.value})} style={s.input} />
                </div>
              )}
            </div>

            {/* Row 3 */}
            <div style={{...s.inputGrid, marginTop: '16px'}}>
              <div>
                <label style={s.label}>{(newBet.type === 'EV' || newBet.type === 'PROMO') ? 'Bookmaker' : 'Bookmaker 1'}</label>
                <div className="bookmaker-dropdown" style={{ position: 'relative' }}>
                  <button type="button" onClick={() => toggleDropdown('bookmaker')} style={s.dropdownButton}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      {newBet.bookmaker && BOOKMAKER_LOGOS[newBet.bookmaker] && <img src={BOOKMAKER_LOGOS[newBet.bookmaker]} alt={newBet.bookmaker} style={{ width: '20px', height: '20px', borderRadius: '4px' }} />}
                      <span>{newBet.bookmaker || 'Select Bookmaker'}</span>
                    </div>
                    <span>{showBookmakerDropdown ? '▲' : '▼'}</span>
                  </button>
                  {showBookmakerDropdown && (
                    <div style={s.dropdownList}>
                      {Object.entries(BOOKMAKERS).map(([country, bookmakers]) => (
                        <div key={country}>
                          <div style={{ padding: '8px 16px', backgroundColor: '#0b1322', color: '#94a3b8', fontSize: '12px', fontWeight: '600' }}>{country}</div>
                          {bookmakers.map(bookmaker => (
                            <div 
                              key={bookmaker} 
                              onClick={() => selectBookmaker(bookmaker)} 
                              style={{
                                ...s.dropdownItem, 
                                backgroundColor: newBet.bookmaker === bookmaker ? '#13203a' : 'transparent',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                padding: '8px 12px',
                                cursor: 'pointer',
                                borderRadius: '4px',
                                border: newBet.bookmaker === bookmaker ? '1px solid #3b82f6' : '1px solid transparent'
                              }} 
                              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#13203a'} 
                              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = newBet.bookmaker === bookmaker ? '#13203a' : 'transparent'}
                            >
                              {BOOKMAKER_LOGOS[bookmaker] ? (
                                <img src={BOOKMAKER_LOGOS[bookmaker]} alt={bookmaker} style={{ width: '24px', height: '24px', borderRadius: '4px', objectFit: 'contain' }} />
                              ) : (
                                <div style={{ width: '24px', height: '24px', backgroundColor: '#0b1322', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', color: '#94a3b8' }}>
                                  {bookmaker.slice(0, 3).toUpperCase()}
                                </div>
                              )}
                              <span style={{ color: '#f1f5f9', fontSize: '14px' }}>{bookmaker}</span>
                            </div>
                          ))}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              
              {(newBet.type === 'ARB' || newBet.type === 'MIDDLE') && (
                <div>
                  <label style={s.label}>Bookmaker 2</label>
                  <div className="bookmaker-dropdown" style={{ position: 'relative' }}>
                    <button type="button" onClick={() => toggleDropdown('bookmaker2')} style={s.dropdownButton}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        {newBet.bookmaker2 && BOOKMAKER_LOGOS[newBet.bookmaker2] && <img src={BOOKMAKER_LOGOS[newBet.bookmaker2]} alt={newBet.bookmaker2} style={{ width: '20px', height: '20px', borderRadius: '4px' }} />}
                        <span>{newBet.bookmaker2 || 'Select Bookmaker 2'}</span>
                      </div>
                      <span>{showBookmaker2Dropdown ? '▲' : '▼'}</span>
                    </button>
                    {showBookmaker2Dropdown && (
                      <div style={s.dropdownList}>
                        {Object.entries(BOOKMAKERS).map(([country, bookmakers]) => (
                          <div key={country}>
                            <div style={{ padding: '8px 16px', backgroundColor: '#0b1322', color: '#94a3b8', fontSize: '12px', fontWeight: '600' }}>{country}</div>
                            {bookmakers.map(bookmaker => (
                              <div 
                                key={bookmaker} 
                                onClick={() => selectBookmaker(bookmaker, 'bookmaker2')} 
                                style={{
                                  ...s.dropdownItem, 
                                  backgroundColor: newBet.bookmaker2 === bookmaker ? '#13203a' : 'transparent',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '8px',
                                  padding: '8px 12px',
                                  cursor: 'pointer',
                                  borderRadius: '4px',
                                  border: newBet.bookmaker2 === bookmaker ? '1px solid #3b82f6' : '1px solid transparent'
                                }} 
                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#13203a'} 
                                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = newBet.bookmaker2 === bookmaker ? '#13203a' : 'transparent'}
                              >
                                {BOOKMAKER_LOGOS[bookmaker] ? (
                                  <img src={BOOKMAKER_LOGOS[bookmaker]} alt={bookmaker} style={{ width: '24px', height: '24px', borderRadius: '4px', objectFit: 'contain' }} />
                                ) : (
                                  <div style={{ width: '24px', height: '24px', backgroundColor: '#0b1322', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', color: '#94a3b8' }}>
                                    {bookmaker.slice(0, 3).toUpperCase()}
                                  </div>
                                )}
                                <span style={{ color: '#f1f5f9', fontSize: '14px' }}>{bookmaker}</span>
                              </div>
                            ))}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div>
                <label style={s.label}>
                  {newBet.type === 'EV' ? 'Odds' : 'Profit ($)'}
                </label>
                <input 
                  type="number" 
                  step="0.01" 
                  placeholder={newBet.type === 'EV' ? "1.90" : "15.50"} 
                  value={newBet.type === 'EV' ? newBet.odds : newBet.profit} 
                  onChange={(e) => setNewBet({...newBet, [newBet.type === 'EV' ? 'odds' : 'profit']: e.target.value})} 
                  style={s.input} 
                />
              </div>

              {newBet.type === 'EV' && (
                <div>
                  <label style={s.label}>Stake ($)</label>
                  <input type="number" step="0.01" placeholder="100" value={newBet.stake} onChange={(e) => setNewBet({...newBet, stake: e.target.value})} style={s.input} />
                </div>
              )}

            </div>

            {/* Row 4 - EV % and Bet Type for EV bets */}
            {newBet.type === 'EV' && (
              <div style={{...s.inputGrid, marginTop: '16px'}}>
                <div>
                  <label style={s.label}>EV %</label>
                  <input type="number" step="0.01" placeholder="5.5" value={newBet.ev_percentage} onChange={(e) => setNewBet({...newBet, ev_percentage: e.target.value})} style={s.input} />
                </div>
                <div>
                  <label style={s.label}>Bet Type</label>
                  <select
                    value={newBet.bet_type_tag}
                    onChange={(e) => setNewBet({...newBet, bet_type_tag: e.target.value})}
                    style={s.select}
                  >
                    <option value="single">Single</option>
                    <option value="multi">Multi</option>
                    <option value="dabble_pickem">PickEm</option>
                    <option value="prop_ev">Prop Outliers</option>
                    <option value="prop_ev_multi">Prop Outlier Multi</option>
                    <option value="odds_screen">Odds Screen</option>
                    <option value="odds_screen_multi">Odds Multi</option>
                    <option value="racing_odds">Racing Odds</option>
                  </select>
                </div>
              </div>
            )}

            {/* Bonus fields for PROMO bets - positioned below row 3 */}
            {newBet.type === 'PROMO' && newBet.promo_type === 'Promo Bet' && (
              <div style={{ marginTop: '16px' }}>
                <label style={{ ...s.checkLabel, marginBottom: '12px' }}>
                  <input
                    type="checkbox"
                    style={s.checkBox}
                    checked={newBet.bonus_received}
                    onChange={(e) => setNewBet({...newBet, bonus_received: e.target.checked})}
                  />
                  Bonus received
                </label>
                {newBet.bonus_received && (
                  <div style={s.inputGrid}>
                    <div>
                      <label style={s.label}>Bonus Amount ($)</label>
                      <input 
                        type="number" 
                        step="0.01" 
                        placeholder="50" 
                        value={newBet.bonus_amount} 
                        onChange={(e) => setNewBet({...newBet, bonus_amount: e.target.value})} 
                        style={s.input} 
                      />
                    </div>
                  </div>
                )}
              </div>
            )}

            {newBet.type === 'PROMO' && newBet.promo_type === 'Bonus Turnover' && (
              <div style={{ marginTop: '16px' }}>
                <label style={s.checkLabel}>
                  <input
                    type="checkbox"
                    style={s.checkBox}
                    checked={newBet.free_bonus}
                    onChange={(e) => setNewBet({...newBet, free_bonus: e.target.checked})}
                  />
                  Free bonus (no qualifying bet)
                </label>
              </div>
            )}

            {/* Qualifying Loss field for MIDDLE bets - positioned at bottom */}
            {newBet.type === 'MIDDLE' && (
              <div style={{ marginTop: '16px' }}>
                <div style={s.inputGrid}>
                  <div>
                    <label style={s.label}>Qualifying Loss ($)</label>
                    <input type="number" step="0.01" placeholder="10" value={newBet.qualifying_loss} onChange={(e) => setNewBet({...newBet, qualifying_loss: e.target.value})} style={s.input} />
                  </div>
                </div>
              </div>
            )}
            
            <div style={{ display: 'flex', gap: '10px', marginTop: '24px', paddingTop: '20px', borderTop: '1px solid #1b2438', justifyContent: 'flex-end' }}>
              <button
                onClick={closeModal}
                style={{
                  padding: '10px 20px',
                  fontSize: '13px',
                  fontWeight: '500',
                  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", sans-serif',
                  background: '#0d1628',
                  color: '#cbd5e1',
                  border: '1px solid #1f2b42',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  transition: 'all 0.15s ease',
                  letterSpacing: '0.01em'
                }}
              >
                Cancel
              </button>
              <button
                onClick={editingBetId ? updateBet : addBet}
                style={{
                  padding: '10px 20px',
                  fontSize: '13px',
                  fontWeight: '500',
                  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", sans-serif',
                  background: '#2563eb',
                  color: '#ffffff',
                  border: '1px solid #3b82f6',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  transition: 'all 0.15s ease',
                  letterSpacing: '0.01em'
                }}
              >
                {editingBetId ? 'Update Bet' : 'Save Bet'}
              </button>
            </div>
          </div>
        </div>
      )}

        {/* Recent Bets Table */}
        <div style={{ borderTop: '1px solid #1b2538', marginTop: '24px', paddingTop: '24px' }}>
        <h2 style={s.subtitle}>Recent {activeTab === 'EV' ? 'EV' : activeTab === 'ARB' ? 'Arbitrage' : activeTab === 'MIDDLE' ? 'Middle' : 'Promo'} Bets</h2>
        <div style={{ 
          height: '600px',
          overflowY: 'scroll', 
          overflowX: 'hidden',
          border: '1px solid #1b2538',
          borderRadius: '8px',
          backgroundColor: 'transparent',
          marginTop: '20px',
          width: '100%',
          display: 'block',
          position: 'relative'
        }}>
          <table style={s.table}>
            <thead>
              <tr>
                <th style={s.th}>Date</th>
                <th style={s.th}>Match</th>
                {activeTab === 'EV' && <th style={s.th}>Market</th>}
                {activeTab === 'EV' && <th style={s.th}>Type</th>}
                {activeTab === 'EV' && <th style={s.th}>Selection</th>}
                {activeTab === 'EV' && <th style={s.th}>Bookmaker</th>}
                {activeTab === 'ARB' && <th style={s.th}>Market</th>}
                {activeTab === 'ARB' && <th style={s.th}>Bookmakers</th>}
                {activeTab === 'MIDDLE' && <th style={s.th}>Market</th>}
                {activeTab === 'MIDDLE' && <th style={s.th}>Bookmakers</th>}
                {activeTab === 'PROMO' && <th style={s.th}>Promo Type</th>}
                {activeTab === 'PROMO' && <th style={s.th}>Bookmaker</th>}
                {activeTab === 'EV' && <th style={s.th}>Odds</th>}
                <th style={s.th}>{activeTab === 'EV' ? 'Stake' : (activeTab === 'ARB' || activeTab === 'MIDDLE') ? 'Total Stake' : 'Stake'}</th>
                {activeTab === 'EV' && <th style={s.th}>EV%</th>}
                <th
                  style={{ ...s.th, cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
                  onClick={() => setStatusFilter(f => f === 'all' ? 'pending' : f === 'pending' ? 'won' : f === 'won' ? 'lost' : 'all')}
                  title="Click to filter by status"
                >
                  Status
                  {statusFilter === 'all' && <span style={{ marginLeft: '5px', opacity: 0.4, fontSize: '10px' }}>▼</span>}
                  {statusFilter === 'pending' && <span style={{ marginLeft: '5px', color: '#f59e0b', fontSize: '10px' }}>● PENDING</span>}
                  {statusFilter === 'won' && <span style={{ marginLeft: '5px', color: '#10b981', fontSize: '10px' }}>● WON</span>}
                  {statusFilter === 'lost' && <span style={{ marginLeft: '5px', color: '#ef4444', fontSize: '10px' }}>● LOST</span>}
                </th>
                <th style={s.th}>Profit</th>
                <th style={s.th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedBets.map((bet, idx) => {
                // Auto-expands while the game is in play and stays through the
                // gap to settlement. Null for anything not started or settled.
                // A multi resolves per leg instead: it has one fixture per leg,
                // so liveGames stores { legEntries } for it and liveLegs reads
                // that back. Exactly one of these is ever non-null.
                const matched = activeTab === 'EV' ? liveGames[bet.id] : null
                const legs = matched ? liveLegs(bet, matched) : null
                const live = (!legs && matched) ? liveProgress(bet, matched) : null
                const expanded = live || (legs && legs.some(l => l.live))
                return (
                <React.Fragment key={bet.id}>
                {/* Both rows of a pair take the SAME stripe colour, so the zebra
                    alternates per bet rather than per row. With five live bets
                    stacked, consecutive pairs still separate; tinting every live
                    pair the same shade made them merge into one block. */}
                {/* No zebra. The 2px divider already separates one bet from the next, and
                    a striped fill on top of it is the same job done twice, which is what
                    made the table read as a slab under transparent panels. */}
                <tr className={expanded ? 'bc-lm-row' : undefined}>
                  <td style={s.td}>{betDate(bet).toLocaleDateString()}</td>
                  {/* One line, same as Selection. Two long team names wrapped to
                      a second line and made that row taller than its neighbours. */}
                  <td style={{ ...s.td, maxWidth: '165px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontWeight: 600, color: '#f1f5f9' }} title={bet.match}>{bet.match}</td>
                  {/* One line, same as Match and Selection. */}
                  {activeTab === 'EV' && (
                    <td style={{ ...s.td, maxWidth: '140px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={formatMarketType(bet.market_type || '') || ''}>
                      {formatMarketType(bet.market_type || '') || '-'}
                    </td>
                  )}
                  {activeTab === 'EV' && (
                    <td style={{ ...s.td, whiteSpace: 'nowrap' }}>
                      <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: '5px',
                        padding: '3px 8px', borderRadius: '4px',
                        fontSize: '11px', fontWeight: '600', letterSpacing: '0.05em', textTransform: 'uppercase',
                        whiteSpace: 'nowrap',
                        backgroundColor: betTypeBadge(bet.bet_type_tag).bg,
                        color: betTypeBadge(bet.bet_type_tag).main,
                        border: `1px solid ${betTypeBadge(bet.bet_type_tag).border}`,
                      }}>
                        {betTypeBadge(bet.bet_type_tag).label}
                      </span>
                    </td>
                  )}
                  {/* Selection: one line, ellipsis, full text on hover. Cutting
                      at a character count instead let a long selection wrap and
                      made the row twice as tall, since 50 characters is wider
                      than the column. maxWidth is what lets a td ellipsis at
                      all: without it the cell just grows to fit. */}
                  {activeTab === 'EV' && (
                    <td style={{ ...s.td, maxWidth: '200px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={bet.selection}>
                      {bet.selection}
                    </td>
                  )}
                  {activeTab === 'EV' && (
                    <td style={s.td}>
                      {BOOKMAKER_LOGOS[bet.bookmaker] ? 
                        <img src={BOOKMAKER_LOGOS[bet.bookmaker]} alt={bet.bookmaker} title={bet.bookmaker} className="bc-trk-book-logo" /> :
                        <span style={{ fontSize: '12px', color: '#94a3b8' }}>{bet.bookmaker}</span>
                      }
                    </td>
                  )}
                  {activeTab === 'ARB' && <td style={s.td}>{formatMarketType(bet.market_type || '') || '-'}</td>}
                  {activeTab === 'ARB' && (
                    <td style={s.td}>
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        {BOOKMAKER_LOGOS[bet.bookmaker] ? 
                          <img src={BOOKMAKER_LOGOS[bet.bookmaker]} alt={bet.bookmaker} title={bet.bookmaker} className="bc-trk-book-logo bc-trk-book-logo--sm" /> :
                          <span style={{ fontSize: '10px', color: '#94a3b8' }}>{bet.bookmaker}</span>
                        }
                        {BOOKMAKER_LOGOS[bet.bookmaker2] ? 
                          <img src={BOOKMAKER_LOGOS[bet.bookmaker2]} alt={bet.bookmaker2} title={bet.bookmaker2} className="bc-trk-book-logo bc-trk-book-logo--sm" /> :
                          <span style={{ fontSize: '10px', color: '#94a3b8' }}>{bet.bookmaker2}</span>
                        }
                      </div>
                    </td>
                  )}
                  {activeTab === 'MIDDLE' && <td style={s.td}>{formatMarketType(bet.market_type || '') || '-'}</td>}
                  {activeTab === 'MIDDLE' && (
                    <td style={s.td}>
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        {BOOKMAKER_LOGOS[bet.bookmaker] ? 
                          <img src={BOOKMAKER_LOGOS[bet.bookmaker]} alt={bet.bookmaker} title={bet.bookmaker} className="bc-trk-book-logo bc-trk-book-logo--sm" /> :
                          <span style={{ fontSize: '10px', color: '#94a3b8' }}>{bet.bookmaker}</span>
                        }
                        {BOOKMAKER_LOGOS[bet.bookmaker2] ? 
                          <img src={BOOKMAKER_LOGOS[bet.bookmaker2]} alt={bet.bookmaker2} title={bet.bookmaker2} className="bc-trk-book-logo bc-trk-book-logo--sm" /> :
                          <span style={{ fontSize: '10px', color: '#94a3b8' }}>{bet.bookmaker2}</span>
                        }
                      </div>
                    </td>
                  )}
                  {activeTab === 'PROMO' && (
                    <td style={s.td}>
                      <span style={{ 
                        ...s.badge, 
                        ...(bet.promo_type === 'Mug Bet' ? s.badgeRed : bet.promo_type === 'Promo Bet' ? s.badgeBlue : s.badgeGreen) 
                      }}>
                        {bet.promo_type}
                      </span>
                    </td>
                  )}
                  {activeTab === 'PROMO' && (
                    <td style={s.td}>
                      {BOOKMAKER_LOGOS[bet.bookmaker] ? 
                        <img src={BOOKMAKER_LOGOS[bet.bookmaker]} alt={bet.bookmaker} title={bet.bookmaker} className="bc-trk-book-logo" /> :
                        <span style={{ fontSize: '12px', color: '#94a3b8' }}>{bet.bookmaker}</span>
                      }
                    </td>
                  )}
                  {activeTab === 'EV' && <td style={s.td}>{convertOdds(bet.odds, oddsFormat)}</td>}
                  {/* Two decimals always, so the column has one shape. Raw, a
                      $100 sat beside a $12.5 and the dollar amounts did not line
                      up even with tabular figures. */}
                  <td style={s.td}>{`$${Number(bet.stake || 0).toFixed(2)}`}</td>
                  {activeTab === 'EV' && <td style={s.td}>{bet.ev_percentage ? `${bet.ev_percentage}%` : '-'}</td>}
                  <td style={s.td}>
                    <span style={{
                      display: 'inline-flex', alignItems: 'center', gap: '5px',
                      padding: '3px 8px', borderRadius: '4px',
                      fontSize: '11px', fontWeight: '600', letterSpacing: '0.05em', textTransform: 'uppercase',
                      backgroundColor: bet.status === 'won' ? 'rgba(16,185,129,0.1)' : bet.horse_result === 'PLACED' ? 'rgba(245,158,11,0.1)' : bet.status === 'lost' ? 'rgba(239,68,68,0.1)' : 'rgba(100,116,139,0.1)',
                      color: bet.status === 'won' ? '#34d399' : bet.horse_result === 'PLACED' ? '#fbbf24' : bet.status === 'lost' ? '#f87171' : '#94a3b8',
                      border: `1px solid ${bet.status === 'won' ? 'rgba(16,185,129,0.25)' : bet.horse_result === 'PLACED' ? 'rgba(245,158,11,0.25)' : bet.status === 'lost' ? 'rgba(239,68,68,0.25)' : 'rgba(100,116,139,0.2)'}`,
                    }}>
                      <span style={{ width: '5px', height: '5px', borderRadius: '50%', backgroundColor: 'currentColor', flexShrink: 0 }} />
                      {bet.horse_result === 'PLACED' ? 'placed' : bet.status}
                    </span>
                  </td>
                  {/* The same green and red as the status badge and the live
                      meters. This cell carried a second, more saturated pair, and
                      two shades of "won" in one row reads as two meanings.
                      Sign goes before the dollar sign: $-5.00 is not how money
                      is written. */}
                  <td style={s.td}>
                    <span style={{ fontWeight: 600, color: bet.actual_profit > 0 ? '#34d399' : bet.actual_profit < 0 ? '#f87171' : '#94a3b8' }}>
                      {bet.actual_profit !== null ? `${bet.actual_profit < 0 ? '-' : ''}$${Math.abs(bet.actual_profit).toFixed(2)}` : '-'}
                    </span>
                  </td>
                  <td style={{ ...s.td, whiteSpace: 'nowrap' }}>
                    <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                      {bet.status === 'pending' && activeTab === 'MIDDLE' && (
                        <>
                          <button onClick={() => updateBetStatus(bet.id, 'won')} title="Won" className="table-btn-won">
                            <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M2 6l3 3 5-5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
                          </button>
                          <button onClick={() => updateBetStatus(bet.id, 'lost')} title="Lost (QL)" className="table-btn-lost" style={{ fontSize: '11px', fontWeight: '600' }}>QL</button>
                        </>
                      )}
                      {bet.status === 'pending' && isHorseBet(bet) && (
                        <>
                          <button onClick={() => updateBetStatus(bet.id, 'won')} title="Won" className="table-btn-won">
                            <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M2 6l3 3 5-5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
                          </button>
                          {bet.bet_type_tag === 'horse_ev' && (
                            <button onClick={() => updateBetStatus(bet.id, 'placed')} title="Placed 2nd or 3rd (bonus back)" className="table-btn-lost" style={{ fontSize: '11px', fontWeight: '700', color: '#fbbf24' }}>P</button>
                          )}
                          <button onClick={() => updateBetStatus(bet.id, 'lost')} title="Lost" className="table-btn-lost">
                            <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M3 3l6 6M9 3l-6 6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/></svg>
                          </button>
                        </>
                      )}
                      {bet.status === 'pending' && (activeTab === 'EV' || activeTab === 'ARB') && (
                        <>
                          <button onClick={() => updateBetStatus(bet.id, 'won')} title="Won" className="table-btn-won">
                            <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M2 6l3 3 5-5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
                          </button>
                          <button onClick={() => updateBetStatus(bet.id, 'lost')} title="Lost" className="table-btn-lost">
                            <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M3 3l6 6M9 3l-6 6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/></svg>
                          </button>
                        </>
                      )}
                      <button onClick={() => editBet(bet.id)} title="Edit bet" className="table-btn-edit">
                        <svg width="13" height="13" viewBox="0 0 13 13" fill="none"><path d="M9 1.5a1.5 1.5 0 012.12 2.12L4.5 10.25H2v-2.5L9 1.5z" stroke="#60a5fa" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/><path d="M7.5 3l2 2" stroke="#60a5fa" strokeWidth="1.4" strokeLinecap="round"/></svg>
                      </button>
                      <button onClick={() => deleteBet(bet.id)} title="Delete bet" className="table-btn-delete">
                        <svg width="13" height="13" viewBox="0 0 13 13" fill="none"><path d="M2 4h9" stroke="#64748b" strokeWidth="1.4" strokeLinecap="round"/><path d="M5 4V2.5h3V4" stroke="#64748b" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/><path d="M3 4l.7 7h5.6L10 4" stroke="#64748b" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/></svg>
                      </button>
                    </div>
                  </td>
                </tr>
                {expanded && (
                  <tr>
                    {/* Full width, but recessed a shade below the row it belongs
                        to. The bet rows keep alternating around it, so stacked
                        live bets still separate without insetting the panel.
                        Bottom border matches the top of the pair (.bc-lm-row in
                        styles/app.css) so the two together bracket it. */}
                    <td colSpan={20} style={{ padding: 0, backgroundColor: '#080e1a', borderBottom: '2px solid #4a6795' }}>
                      {legs
                        ? <LiveMultiMeter legs={legs} open={openMultis.has(bet.id)} onToggle={() => toggleMulti(bet.id)} />
                        : <LiveBetMeter live={live} bet={bet} />}
                    </td>
                  </tr>
                )}
                </React.Fragment>
                )
              })}
            </tbody>
          </table>
        </div>

        {/* Pagination Controls */}
        {currentBets.length > itemsPerPage && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '16px', padding: '12px 4px' }}>
            <div style={{ fontSize: '12.5px', color: '#8a8f96', fontFamily: MONO, fontVariantNumeric: 'tabular-nums' }}>
              Showing {(currentPage - 1) * itemsPerPage + 1}–{Math.min(currentPage * itemsPerPage, currentBets.length)} of {currentBets.length} bets
            </div>

            {/* The same first/prev/count/next/last control every other tool page uses.
                No window.scrollTo on these, unlike the tool pages: this table lives in
                its own 600px scroll container, so scrolling the window would jump the
                user away from the thing they just paged. */}
            <div className="bc-pagination" style={{ padding: 0 }}>
              <button
                className="bc-page-arrow"
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                data-tooltip="First page"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="11 18 5 12 11 6"/><polyline points="19 18 13 12 19 6"/></svg>
              </button>
              <button
                className="bc-page-arrow"
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                data-tooltip="Previous page"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
              </button>
              <span className="bc-page-info">{currentPage} / {totalPages}</span>
              <button
                className="bc-page-arrow"
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                data-tooltip="Next page"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
              </button>
              <button
                className="bc-page-arrow"
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
                data-tooltip="Last page"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="5 18 11 12 5 6"/><polyline points="13 18 19 12 13 6"/></svg>
              </button>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '12.5px', color: '#8a8f96' }}>Per page:</span>
              <select
                value={itemsPerPage}
                onChange={(e) => { const v = Number(e.target.value); setItemsPerPage(v); localStorage.setItem('tracker_items_per_page', v); setCurrentPage(1) }}
                style={{ padding: '6px 10px', backgroundColor: 'transparent', color: '#f1f5f9', border: '1px solid #1b2538', borderRadius: '6px', cursor: 'pointer', fontSize: '12.5px', fontFamily: MONO, fontVariantNumeric: 'tabular-nums' }}
              >
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
          </div>
        )}

        </div>
      </div>

      {/* Export Confirm Modal */}
      {showExportConfirm && (
        <div style={s.modalOverlay} onClick={() => setShowExportConfirm(false)}>
          <div style={{ ...s.modal, maxWidth: '380px' }} onClick={e => e.stopPropagation()}>
            <div style={s.modalHeader}>
              <span style={{ fontSize: '20px', fontWeight: '700', color: '#f1f5f9', letterSpacing: '-0.01em' }}>Export CSV</span>
              <button onClick={() => setShowExportConfirm(false)} style={s.closeBtn}>×</button>
            </div>

            <p style={{ fontSize: '13px', color: '#8a8f96', margin: '0 0 24px 0' }}>
              Export all <span style={{ color: '#f1f5f9', fontWeight: '600' }}>
                {activeTab === 'EV' ? allEvBets.length : activeTab === 'ARB' ? allArbBets.length : activeTab === 'MIDDLE' ? allMiddleBets.length : allPromoBets.length}
              </span> {activeTab === 'EV' ? 'EV' : activeTab === 'ARB' ? 'Arbitrage' : activeTab === 'MIDDLE' ? 'Middle' : 'Promo'} bets as a CSV file?
            </p>

            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={() => setShowExportConfirm(false)}
                style={{ flex: 1, padding: '11px', background: '#0d1628', color: '#cbd5e1', border: '1px solid #1f2b42', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: '500' }}
              >
                Cancel
              </button>
              <button
                onClick={() => { executeExport(); setShowExportConfirm(false) }}
                style={{ flex: 2, padding: '11px', background: '#2563eb', color: '#ffffff', border: '1px solid #3b82f6', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: '600', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
              >
                <svg width="13" height="13" viewBox="0 0 13 13" fill="none"><path d="M6.5 2v7M3.5 6l3 3 3-3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/><path d="M2 10.5h9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
                Export CSV
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Import Info Modal */}
      {showImportInfo && (
        <div style={s.modalOverlay} onClick={() => setShowImportInfo(false)}>
          <div style={{ ...s.modal, maxWidth: '580px' }} onClick={e => e.stopPropagation()}>

            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '20px' }}>
              <div>
                <h2 style={{ fontSize: '20px', fontWeight: '700', color: '#f1f5f9', margin: '0 0 4px 0', letterSpacing: '-0.01em' }}>
                  Import {activeTab === 'EV' ? 'EV' : activeTab === 'ARB' ? 'Arbitrage' : activeTab === 'MIDDLE' ? 'Middle' : 'Promo'} Bets
                </h2>
                <p style={{ fontSize: '13px', color: '#8a8f96', margin: 0 }}>CSV must include a header row · Max 1000 rows</p>
              </div>
              <button onClick={() => setShowImportInfo(false)} style={s.closeBtn}>×</button>
            </div>

            {/* Fields table */}
            <div style={{ border: '1px solid #1b2438', borderRadius: '8px', overflow: 'hidden', marginBottom: '20px' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ backgroundColor: '#0d1628' }}>
                    <th style={{ padding: '9px 14px', textAlign: 'left', fontSize: '11px', fontWeight: '600', color: '#8a8f96', textTransform: 'uppercase', letterSpacing: '0.06em', width: '26%', borderRight: '1px solid #1b2438' }}>Field</th>
                    <th style={{ padding: '9px 14px', textAlign: 'left', fontSize: '11px', fontWeight: '600', color: '#8a8f96', textTransform: 'uppercase', letterSpacing: '0.06em', borderRight: '1px solid #1b2438' }}>Description</th>
                    <th style={{ padding: '9px 14px', textAlign: 'left', fontSize: '11px', fontWeight: '600', color: '#8a8f96', textTransform: 'uppercase', letterSpacing: '0.06em', width: '22%' }}>Example</th>
                  </tr>
                </thead>
                <tbody>
                  {IMPORT_FIELDS[activeTab].map((f, i) => (
                    <tr key={f.field} style={{ borderTop: '1px solid #1b2438' }}>
                      <td style={{ padding: '9px 14px', borderRight: '1px solid #1b2438' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <code style={{ fontSize: '12px', color: '#93c5fd', fontFamily: 'monospace', fontWeight: '500' }}>{f.field}</code>
                          {!f.required && <span style={{ fontSize: '10px', color: '#475569', marginLeft: '2px' }}>optional</span>}
                        </div>
                      </td>
                      <td style={{ padding: '9px 14px', fontSize: '12px', color: '#94a3b8', borderRight: '1px solid #1b2438' }}>{f.desc}</td>
                      <td style={{ padding: '9px 14px', fontSize: '12px', color: '#94a3b8', fontFamily: 'monospace' }}>{f.example}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <button
              onClick={() => importFileRef.current?.click()}
              style={{ width: '100%', padding: '11px', background: '#2563eb', color: '#f1f5f9', border: '1px solid #3b82f6', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: '600', letterSpacing: '0.02em' }}
            >
              Choose CSV File
            </button>
          </div>
        </div>
      )}

      {/* Import Confirm Modal */}
      {showImportConfirm && importPreview && (
        <div style={s.modalOverlay}>
          <div style={{ ...s.modal, maxWidth: '460px' }}>
            <div style={s.modalHeader}>
              <span style={{ fontSize: '20px', fontWeight: '700', color: '#f1f5f9', letterSpacing: '-0.01em' }}>Confirm Import</span>
              <button onClick={() => { setShowImportConfirm(false); setImportPreview(null) }} style={s.closeBtn}>×</button>
            </div>

            {/* Summary row */}
            <div style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '12px', padding: '14px 16px', backgroundColor: 'rgba(16,185,129,0.10)', border: '1px solid rgba(16,185,129,0.25)', borderRadius: '8px' }}>
                <span style={{ fontSize: '26px', fontWeight: '700', color: '#34d399', lineHeight: 1 }}>{importPreview.valid.length}</span>
                <span style={{ fontSize: '13px', color: '#86efac' }}>ready to import</span>
              </div>
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '12px', padding: '14px 16px', backgroundColor: 'rgba(239,68,68,0.10)', border: '1px solid rgba(239,68,68,0.25)', borderRadius: '8px' }}>
                <span style={{ fontSize: '26px', fontWeight: '700', color: '#f87171', lineHeight: 1 }}>{importPreview.skipped.length}</span>
                <span style={{ fontSize: '13px', color: '#fca5a5' }}>rows skipped</span>
              </div>
            </div>

            {/* Skipped rows table */}
            {importPreview.skipped.length > 0 && (
              <div style={{ border: '1px solid #1b2438', borderRadius: '8px', overflow: 'hidden', marginBottom: '20px', maxHeight: '200px', overflowY: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#0d1628', position: 'sticky', top: 0 }}>
                      <th style={{ padding: '8px 12px', textAlign: 'left', fontSize: '11px', fontWeight: '600', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', width: '60px', borderRight: '1px solid #1b2438' }}>Row</th>
                      <th style={{ padding: '8px 12px', textAlign: 'left', fontSize: '11px', fontWeight: '600', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Reason</th>
                    </tr>
                  </thead>
                  <tbody>
                    {importPreview.skipped.map((sk, i) => (
                      <tr key={i} style={{ borderTop: '1px solid #1b2438' }}>
                        <td style={{ padding: '8px 12px', fontSize: '12px', color: '#64748b', fontFamily: 'monospace', borderRight: '1px solid #1b2438' }}>{sk.row}</td>
                        <td style={{ padding: '8px 12px', fontSize: '12px', color: '#94a3b8' }}>{sk.reason}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={() => { setShowImportConfirm(false); setImportPreview(null) }}
                style={{ flex: 1, padding: '11px', background: '#0d1628', color: '#cbd5e1', border: '1px solid #1f2b42', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: '500' }}
              >
                Cancel
              </button>
              <button
                onClick={executeImport}
                disabled={importLoading || importPreview.valid.length === 0}
                style={{ flex: 2, padding: '11px', background: '#2563eb', color: '#f1f5f9', border: '1px solid #3b82f6', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: '600', opacity: importLoading || importPreview.valid.length === 0 ? 0.6 : 1 }}
              >
                {importLoading ? 'Importing...' : `Import ${importPreview.valid.length} Bets`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Result Toast */}
      {importResult && (
        <div style={{ position: 'fixed', bottom: '24px', right: '24px', backgroundColor: 'transparent', border: '1px solid #1b2538', borderRadius: '8px', padding: '14px 20px', zIndex: 2000, boxShadow: '0 8px 24px rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#34d399', display: 'inline-block', flexShrink: 0 }} />
          <span style={{ color: '#f1f5f9', fontWeight: '600', fontSize: '14px' }}>Imported {importResult.imported} bets</span>
          {importResult.skipped > 0 && <span style={{ color: '#64748b', fontSize: '13px' }}>· {importResult.skipped} skipped</span>}
        </div>
      )}

    </div>
  )
}